// ====================
// CUSTOM PODCAST HANDLER
// Integrates Inner Loop modules with podcast generation
// Inner Loop is ONLY for podcasts - no detection needed!
// ====================

import { 
  parseDuration,
  validateDuration
} from './Azurechatbot';
import { SecureAPI } from '../config.js';

import { getTrafficData, formatTrafficForPodcast } from './trafficHandler';
// ✅ HELPER FUNCTIONS
import { fetchLocalNews, formatLocalNewsForPodcast } from './localNewsHandler';
const getTimeOfDay = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
};

const getTimeGreeting = () => {
  const timeOfDay = getTimeOfDay();
  const greetings = {
    morning: 'Good morning',
    afternoon: 'Good afternoon',
    evening: 'Good evening',
    night: 'Hey'
  };
  return greetings[timeOfDay];
};
const detectTimeQuestion = (text) => {
  if (!text) return false;
  
  const lower = text.toLowerCase();
  
  // Timing questions
  if (lower.match(/\b(when|what time|schedule|this weekend|next week|tonight|today)\b/)) {
    return true;
  }
  
  // Sports/event results
  if (lower.match(/\b(who won|winner|score|result|game|match)\b/)) {
    return true;
  }
  
  // News/updates
  if (lower.match(/\b(latest|breaking|news today|what happened|update on)\b/)) {
    return true;
  }
  
  return false;
};
const shouldSearchWeb = (text) => {
  if (!text) return false;
  
  const lower = text.toLowerCase();
  
  // ✅ GENERAL KNOWLEDGE - Don't search (AI knows this)
  const generalKnowledgePatterns = [
    /\b(what is|explain|define|how does|why does)\b.*\b(evolution|gravity|photosynthesis|democracy|capitalism)/i,
    /\b(natural selection|theory of|history of|concept of)\b/i,
    /\b(explain|tell me about|what are)\b.*\b(atoms|molecules|cells|DNA)/i,
    /podcast (on|about) (philosophy|science|history|economics)/i
  ];
  
  for (const pattern of generalKnowledgePatterns) {
    if (pattern.test(text)) {
      console.log('🧠 General knowledge detected - no web search needed');
      return false;
    }
  }
  
  // ✅ TIME-SENSITIVE - Do search
  const timeSensitivePatterns = [
    /\b(when|what time|schedule|this weekend|next week|tonight|today|tomorrow)\b/i,
    /\b(who won|winner|score|result|latest game|last night's game)\b/i,
    /\b(latest|breaking|news today|what happened|just announced|update on)\b/i,
    /\b(current|right now|as of|recent)\b/i,
    /\b(weather|forecast|temperature)\b.*\b(today|tomorrow|this week)\b/i,
    /\b(event|concert|game|match)\b.*\b(this|next|upcoming)\b/i
  ];
  
  for (const pattern of timeSensitivePatterns) {
    if (pattern.test(text)) {
      console.log('⏰ Time-sensitive question detected - searching web');
      return true;
    }
  }
  
  return false;
};

/**
 * ✅ ENHANCED: Smart search decision with context awareness
 * Expands vague queries and verifies event existence
 */
const analyzeQueryNeedsWebSearch = async (userQuery) => {
  if (!userQuery || !userQuery.trim()) {
    return { needsSearch: false };
  }
  
  try {
    console.log('🤔 Analyzing query with context awareness:', userQuery);
    
    // ✅ USE SECUREAPI
    const data = await SecureAPI.callAzureOpenAI([
      {
        role: 'system',
        content: `You are an intelligent search decision engine with CONTEXT AWARENESS.

CRITICAL CAPABILITY: You can understand VAGUE queries and determine if they need real-time verification.

🎯 VAGUE QUERY DETECTION:
When users ask vague questions like:
- "tell me about the afcon match" (which match? when?)
- "world cup update" (is there a world cup happening now?)
- "latest game" (which sport? which teams?)
- "the match today" (unspecified)
- "trump news" (generic - could mean anything)

YOU MUST:
1. Recognize this is vague/time-sensitive
2. Set needsSearch: true
3. Create a SMART search query that includes:
   - Current date context (today, this week, 2025)
   - Specificity (match results, latest news, recent update)
   - Sport/context if inferable

🔍 SEARCH QUERY EXAMPLES:

User: "tell me about the afcon match"
❌ BAD: searchQuery: "afcon match" (too vague)
✅ GOOD: searchQuery: "afcon 2025 latest match results today" (specific + dated)

User: "world cup update"
❌ BAD: searchQuery: "world cup" (could be any year/sport)
✅ GOOD: searchQuery: "fifa world cup 2025 latest news today" (specific + current)

User: "latest game"
❌ BAD: searchQuery: "latest game" (no context)
✅ GOOD: searchQuery: "latest sports game results today" (broader + current)

User: "the trump trial"
❌ BAD: searchQuery: "trump trial" (could be old news)
✅ GOOD: searchQuery: "donald trump trial latest updates 2025" (specific + current)

User: "stock market"
❌ BAD: searchQuery: "stock market" (too generic)
✅ GOOD: searchQuery: "stock market news today january 2025" (specific + dated)

🎯 DECISION RULES:

✅ SEARCH NEEDED (needsSearch: true):
- ANY sports/match question (could be happening now)
- ANY news question (needs current verification)
- Vague questions about events/people (need specificity)
- Questions with "latest", "today", "now", "current"
- Questions about ongoing situations
- Questions about people's current status
- ANY question where the answer could have changed recently

❌ NO SEARCH NEEDED (needsSearch: false):
- Historical facts with fixed answers: "when was America founded"
- Scientific concepts: "explain photosynthesis"
- Definitions: "what is democracy"
- Mathematical concepts: "explain calculus"
- Completed historical events: "who won World War 2"
- Theoretical questions: "what if dinosaurs didn't go extinct"

🔄 VERIFICATION STRATEGY:
For vague questions, explain WHY you need to search:
- "This could be referring to a recent/ongoing event"
- "Need to verify if this is currently happening"
- "Multiple possible interpretations, need current context"

RESPONSE FORMAT:
{
  "needsSearch": true/false,
  "reasoning": "Why search is/isn't needed",
  "searchQuery": "optimized search query with context and date" (if needsSearch is true),
  "category": "sports/news/tech/weather/general/etc",
  "confidence": "high/medium/low",
  "queryType": "vague/specific/ambiguous",
  "eventVerification": "needs_verification/clearly_current/clearly_historical"
}

📅 CURRENT CONTEXT:
- Today's date: ${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
- Current year: 2025
- Use this context to make search queries specific

EXAMPLES:

Query: "afcon match update"
{
  "needsSearch": true,
  "reasoning": "Vague sports query - could be referring to ongoing/recent AFCON matches. Need to verify if tournament is currently happening and get latest results.",
  "searchQuery": "afcon 2025 latest match results today",
  "category": "sports",
  "confidence": "high",
  "queryType": "vague",
  "eventVerification": "needs_verification"
}

Query: "world cup news"
{
  "needsSearch": true,
  "reasoning": "Vague - need to check if World Cup is currently happening or if this refers to recent news",
  "searchQuery": "fifa world cup 2025 latest news updates",
  "category": "sports",
  "confidence": "high",
  "queryType": "vague",
  "eventVerification": "needs_verification"
}

Query: "who won the 2022 world cup"
{
  "needsSearch": false,
  "reasoning": "Specific historical event with a fixed answer (Argentina won). No need to search.",
  "confidence": "high",
  "queryType": "specific",
  "eventVerification": "clearly_historical"
}

Query: "explain gravity"
{
  "needsSearch": false,
  "reasoning": "Scientific concept with established answer. No current events involved.",
  "confidence": "high",
  "queryType": "specific",
  "eventVerification": "clearly_historical"
}`
      },
      {
        role: 'user',
        content: `Query: "${userQuery}"

Analyze this query:
1. Is it vague or specific?
2. Could it be referring to something happening NOW or RECENTLY?
3. Do I need current information to answer accurately?
4. If vague, create a SMART search query with date context

Today is ${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}.

Respond in JSON format only.`
      }
    ], {
      temperature: 0.3,
      max_tokens: 400,
      response_format: { type: "json_object" }
    });
    
    const content = data.choices[0].message.content;
    const analysis = JSON.parse(content);
    
    console.log('\n🧠 ENHANCED AI ANALYSIS:');
    console.log(`   ├─ Needs Search: ${analysis.needsSearch ? '✅ YES' : '❌ NO'}`);
    console.log(`   ├─ Query Type: ${analysis.queryType || 'unknown'}`);
    console.log(`   ├─ Event Status: ${analysis.eventVerification || 'unknown'}`);
    console.log(`   ├─ Confidence: ${analysis.confidence || 'medium'}`);
    console.log(`   ├─ Category: ${analysis.category || 'general'}`);
    console.log(`   └─ Reasoning: ${analysis.reasoning}`);
    
    if (analysis.needsSearch) {
      console.log(`\n🔍 SMART SEARCH QUERY: "${analysis.searchQuery}"`);
      console.log(`   Original: "${userQuery}"`);
      console.log(`   Enhanced: Added context + specificity + date`);
    }
    
    return {
      needsSearch: analysis.needsSearch,
      searchQuery: analysis.searchQuery || userQuery,
      category: analysis.category || 'general',
      reasoning: analysis.reasoning,
      confidence: analysis.confidence,
      originalQuery: userQuery,
      queryType: analysis.queryType,
      eventVerification: analysis.eventVerification
    };
    
  } catch (error) {
    console.error('❌ AI analysis error:', error);
    return fallbackSearchDetection(userQuery);
  }
};

/**
 * ✅ ENHANCED: Fallback detection with better vague query handling
 */
const fallbackSearchDetection = (text) => {
  const lower = text.toLowerCase();
  
  // ✅ Sports/event indicators (ALWAYS search)
  const sportsPatterns = [
    /\b(match|game|tournament|championship|cup|league|playoff)\b/i,
    /\b(afcon|world cup|euro|olympics|super bowl|nba|nfl|mlb|nhl)\b/i,
    /\b(score|result|winner|final|live)\b/i
  ];
  
  for (const pattern of sportsPatterns) {
    if (pattern.test(text)) {
      return {
        needsSearch: true,
        searchQuery: `${text} latest results today`,
        category: 'sports',
        reasoning: 'Sports/event query detected - needs current verification',
        confidence: 'high',
        originalQuery: text,
        queryType: 'vague',
        eventVerification: 'needs_verification'
      };
    }
  }
  
  // ✅ Time indicators (search needed)
  const timeIndicators = [
    'today', 'tonight', 'yesterday', 'this week', 'this month', 
    'recent', 'latest', 'current', 'now', 'just', 'breaking',
    'what happened', 'what\'s happening', 'right now', 'update'
  ];
  
  const hasTimeIndicator = timeIndicators.some(indicator => lower.includes(indicator));
  
  if (hasTimeIndicator) {
    return {
      needsSearch: true,
      searchQuery: `${text} ${new Date().getFullYear()}`,
      category: 'general',
      reasoning: 'Time-sensitive indicator detected',
      confidence: 'medium',
      originalQuery: text,
      queryType: 'vague',
      eventVerification: 'needs_verification'
    };
  }
  
  // ✅ News patterns (search needed)
  const newsPattern = /\b(latest|news|update|announcement)\b.*\b(on|about|from|in)\b/i;
  if (newsPattern.test(text)) {
    return {
      needsSearch: true,
      searchQuery: text,
      category: 'news',
      reasoning: 'Request for latest information',
      confidence: 'medium',
      originalQuery: text,
      queryType: 'vague',
      eventVerification: 'needs_verification'
    };
  }
  
  // ✅ Default: assume no search needed
  return {
    needsSearch: false,
    reasoning: 'Appears to be general knowledge question',
    confidence: 'low',
    queryType: 'ambiguous',
    eventVerification: 'unclear'
  };
};

/**
 * ✅ ENHANCED: Search with better error handling and "no results" intelligence
 */
const performUniversalWebSearch = async (searchInfo) => {
  const { searchQuery, category } = searchInfo;
  
  try {
    console.log(`🔍 Universal web search [${category}]:`, searchQuery);
    
    const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'fir-7c9c9';
    const functionUrl = `https://us-central1-${projectId}.cloudfunctions.net/universalWebSearch`;
    
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: { 
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        query: searchQuery,
        category: category,
        maxResults: 5
      }),
      mode: 'cors',
      cache: 'no-cache'
    });
    
    console.log(`   Response status: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ Search failed: HTTP ${response.status}`);
      console.error(`   Error: ${errorText.substring(0, 200)}`);
      return null;
    }
    
    const data = await response.json();
    
    if (data.success) {
      console.log(`✅ Found ${data.results?.length || 0} results`);
      
      // ✅ NEW: Check if results are actually relevant
      if (!data.results || data.results.length === 0) {
        console.warn('⚠️ Search returned 0 results - event may not exist or not happening');
        return {
          success: true,
          results: [],
          noResults: true,
          originalQuery: searchInfo.originalQuery,
          searchQuery: searchQuery,
          category: category
        };
      }
      
      return {
        ...data,
        category: category,
        searchQuery: searchQuery,
        noResults: false
      };
    } else {
      console.warn(`⚠️ Search returned success:false - ${data.message || 'Unknown error'}`);
      return {
        success: true,
        results: [],
        noResults: true,
        originalQuery: searchInfo.originalQuery,
        searchQuery: searchQuery,
        category: category
      };
    }
    
  } catch (error) {
    console.error('❌ Universal search error:', error);
    return null;
  }
};

/**
 * ✅ ENHANCED: Format with "no results" intelligence
 */
const formatSearchResultsForPodcast = (searchData, userName) => {
  if (!searchData) {
    return {
      type: 'web_answer',
      content: `I couldn't connect to search right now.`,
      category: 'error',
      noResults: true
    };
  }
  
  // ✅ NEW: Handle "no results" intelligently
  if (searchData.noResults || !searchData.results || searchData.results.length === 0) {
    console.log('⚠️ No search results found - event may not be happening');
    
    return {
      type: 'web_answer',
      content: null, // ✅ Signal to use AI knowledge instead
      noResults: true,
      originalQuery: searchData.originalQuery,
      searchQuery: searchData.searchQuery,
      category: searchData.category,
      aiShouldAnswer: true // ✅ Flag to tell AI to use its knowledge
    };
  }
  
  // ✅ Extract facts from results
  const facts = searchData.results.slice(0, 5).map(r => ({
    title: r.title,
    snippet: r.snippet || r.description,
    date: r.datePublished
  }));
  
  return {
    type: 'web_answer',
    facts: facts,
    category: searchData.category || 'general',
    noResults: false
  };
};

/**
 * ✅ NEW: Smart content enrichment with fallback to AI knowledge
 */
const enrichContentWithWebSearch = async (promptText, sections, userName) => {
  // Step 1: Check if web search is needed
  const searchAnalysis = await analyzeQueryNeedsWebSearch(promptText);
  
  if (!searchAnalysis.needsSearch) {
    console.log('✅ AI can answer this without web search');
    return sections;
  }
  
  console.log('🌐 Web search needed, fetching real-time info...');
  console.log(`   Original query: "${searchAnalysis.originalQuery}"`);
  console.log(`   Enhanced query: "${searchAnalysis.searchQuery}"`);
  
  // Step 2: Perform search
  const searchResults = await performUniversalWebSearch(searchAnalysis);
  
  // ✅ Step 3: Handle "no results" intelligently
  if (!searchResults || searchResults.noResults) {
    console.log('📝 No web results found - will use AI knowledge instead');
    
    // ✅ Add a section that tells the AI to use its training data
    sections.unshift({
      type: 'ai_knowledge_answer',
      content: `Answer this question using your training data: ${searchAnalysis.originalQuery}`,
      originalQuery: searchAnalysis.originalQuery,
      searchAttempted: true,
      noWebResults: true,
      reasoning: 'Web search found no current results, so answer from training knowledge if you have reliable information about this topic.'
    });
    
    console.log('✅ AI knowledge fallback section added');
    return sections;
  }
  
  // Step 4: Format results for podcast
  const formattedContent = formatSearchResultsForPodcast(searchResults, userName);
  
  if (formattedContent.aiShouldAnswer) {
    // Same as no results - use AI knowledge
    sections.unshift({
      type: 'ai_knowledge_answer',
      content: `Answer this question using your training data: ${searchAnalysis.originalQuery}`,
      originalQuery: searchAnalysis.originalQuery,
      searchAttempted: true,
      noWebResults: true
    });
  } else {
    // Add web search content
    sections.unshift(formattedContent);
    console.log('✅ Web search content added to podcast');
  }
  
  return sections;
};

/**
 * ✅ SIMPLIFIED: Just extract key facts from search results
 * No pre-formatting, no source citations
 * generatePodcastDialogue will make it natural
 */
const extractSearchFacts = (searchResults) => {
  if (!searchResults || !searchResults.results || searchResults.results.length === 0) {
    return null;
  }
  
  // Just extract the top 3-5 results with their key info
  const facts = searchResults.results.slice(0, 5).map(result => ({
    title: result.title,
    snippet: result.snippet || result.description,
    date: result.datePublished
  }));
  
  return facts;
};

/**
 * ✅ SIMPLIFIED: Build topic with raw search facts
 * No instructions about citations - let AI handle it naturally
 */
const buildTopicWithSearchFacts = (originalTopic, searchFacts) => {
  if (!searchFacts || searchFacts.length === 0) {
    return originalTopic;
  }
  
  // Just append the facts, let generatePodcastDialogue make it natural
  let enrichedTopic = `Topic: ${originalTopic}\n\nCurrent Information:\n`;
  
  searchFacts.forEach((fact, idx) => {
    enrichedTopic += `\n${idx + 1}. ${fact.title}`;
    if (fact.snippet) {
      enrichedTopic += `\n   ${fact.snippet}`;
    }
  });
  
  enrichedTopic += `\n\nDiscuss this information naturally in a conversational way.`;
  
  return enrichedTopic;
};


/**
 * ✅ HELPER: Extract main topic from search query
 */
const extractTopic = (query) => {
  if (!query) return 'that topic';
  
  // Remove common words
  const cleaned = query
    .replace(/\b(latest|news|update|what's|what is|tell me about|happening|in|on|about|the)\b/gi, '')
    .trim();
  
  return cleaned || query;
};


const fetchWeeklyStockPerformance = async () => {
  try {
    console.log('📊 Fetching weekly stock performance...');
    
    // This would be a new backend function you'd create
    const response = await fetch(
      'https://us-central1-fir-7c9c9.cloudfunctions.net/fetchStocksWeekly',
      {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      }
    );
    
    if (!response.ok) {
      console.error(`❌ Backend returned HTTP ${response.status}`);
      return null;
    }
    
    const data = await response.json();
    
    if (data.success) {
      console.log(`✅ Weekly performance data loaded`);
      return data;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error fetching weekly data:', error);
    return null;
  }
};

const validateDialogueStructure = (dialogue) => {
  // 1. Basic structure validation
  if (!Array.isArray(dialogue)) {
    throw new Error('Dialogue must be an array');
  }
  
  if (dialogue.length === 0) {
    throw new Error('Dialogue array is empty');
  }
  
  if (dialogue.length < 10) {
    console.warn(`⚠️ Dialogue is very short: only ${dialogue.length} exchanges`);
  }
  
  // 2. Validate and fix each entry
  for (let i = 0; i < dialogue.length; i++) {
    const item = dialogue[i];
    
    // Validate speaker
    if (!item.speaker || typeof item.speaker !== 'string') {
      throw new Error(`Entry ${i}: Missing or invalid speaker`);
    }
    
    // Validate dialogue line (different rules for background reactions)
    const isBackground = item.background === true;
    const minLength = isBackground ? 2 : 5;
    
    if (!item.line || typeof item.line !== 'string') {
      console.warn(`⚠️ Entry ${i}: Missing dialogue line, adding fallback`);
      item.line = isBackground ? "Mhm" : "I see what you mean.";
    } else if (item.line.trim().length < minLength) {
      console.warn(`⚠️ Entry ${i}: Line too short (${item.line.length} chars), adding fallback`);
      item.line = isBackground ? "Mhm" : "That's interesting.";
    }
    
    // Validate timestamp
    if (typeof item.timestamp !== 'number' || item.timestamp < 0) {
      console.warn(`⚠️ Entry ${i}: Invalid timestamp, calculating from position`);
      // Estimate based on previous entry or position
      const prevTimestamp = i > 0 ? dialogue[i - 1].timestamp : 0;
      const estimatedDuration = isBackground ? 1 : 5;
      item.timestamp = prevTimestamp + estimatedDuration;
    }
    
    // Validate keywords
    if (!item.keywords || !Array.isArray(item.keywords)) {
      console.warn(`⚠️ Entry ${i}: Missing keywords array, adding empty array`);
      item.keywords = [];
    }
    
    // Clean keywords (remove invalid entries)
    item.keywords = item.keywords.filter(k => 
      typeof k === 'string' && k.trim().length > 0
    );
  }
  
  // 3. Validate required sections
  const hasIntro = dialogue.some(d => d.section === 'Introduction');
  const hasWrapUp = dialogue.some(d => d.section === 'Wrap Up');
  
  if (!hasIntro) {
    console.warn('⚠️ Missing Introduction section, adding to first line');
    dialogue[0].section = 'Introduction';
  }
  
  if (!hasWrapUp && dialogue.length > 5) {
    console.warn('⚠️ Missing Wrap Up section, adding to last line');
    dialogue[dialogue.length - 1].section = 'Wrap Up';
  }
  
  // 4. Final validation summary
  console.log(`✅ Validation complete: ${dialogue.length} entries validated`);
  const backgroundCount = dialogue.filter(d => d.background === true).length;
  const sectionCount = dialogue.filter(d => d.section).length;
  console.log(`   - Background reactions: ${backgroundCount}`);
  console.log(`   - Sections marked: ${sectionCount}`);
  
  return true;
};
// ✅ Map host labels to actual names
const getHostName = (hostLabel) => {
  const nameMap = {
    'Host 1': 'Emma',
    'Host 2': 'James', 
    'Host 3': 'Jackie',
    'Host 4': 'Kenny'
  };
  return nameMap[hostLabel] || hostLabel;
};
// ✅ Use API to find nearby cities automatically (works anywhere!)
const findNearbyCities = async (lat, lon, weatherApiKey) => {
  try {
    console.log('🌍 Finding nearby cities for coordinates:', lat, lon);
    
    // Use OpenWeatherMap's "find" endpoint (finds cities in radius)
    const response = await fetch(
      `https://api.openweathermap.org/data/2.5/find?lat=${lat}&lon=${lon}&cnt=5&units=imperial&appid=${weatherApiKey}`
    );
    
    if (!response.ok) {
      throw new Error('Failed to find nearby cities');
    }
    
    const data = await response.json();
    
    // Filter out the main city (first result) and get 2-3 nearby
    const nearbyCities = data.list
      .slice(1, 4) // Skip first (that's the main location), take next 2-3
      .map(city => ({
        name: city.name,
        temp: Math.round(city.main.temp),
        condition: city.weather[0].main.toLowerCase(),
        distance: Math.round(city.distance / 1609.34), // Convert meters to miles
        lat: city.coord.lat,
        lon: city.coord.lon
      }));
    
    console.log('✅ Found nearby cities:', nearbyCities);
    return nearbyCities;
    
  } catch (error) {
    console.error('❌ Error finding nearby cities:', error);
    return [];
  }
};

// ✅ Generate natural comparison text
const generateWeatherComparison = (mainTemp, mainCondition, nearbyCity) => {
  const tempDiff = nearbyCity.temp - mainTemp;
  let comparison = '';
  
  // Temperature comparison
  if (Math.abs(tempDiff) < 3) {
    comparison = `about the same at ${nearbyCity.temp}°F`;
  } else if (tempDiff > 0) {
    comparison = `${Math.abs(tempDiff)} degrees warmer at ${nearbyCity.temp}°F`;
  } else {
    comparison = `${Math.abs(tempDiff)} degrees cooler at ${nearbyCity.temp}°F`;
  }
  
  // Add condition if different
  if (nearbyCity.condition !== mainCondition.toLowerCase()) {
    comparison += ` and ${nearbyCity.condition}`;
  }
  
  // Optionally add distance (50% of the time)
  if (nearbyCity.distance && Math.random() > 0.5) {
    comparison += `, about ${nearbyCity.distance} miles away`;
  }
  
  return {
    ...nearbyCity,
    comparison: comparison
  };
};
// ✅ Randomize starting hosts for variety - MOVED TO TOP LEVEL
const getRandomStartingHosts = () => {
  const allHosts = ["Host 1", "Host 2", "Host 3", "Host 4"];
  const shuffled = allHosts.sort(() => Math.random() - 0.5);
  return [shuffled[0], shuffled[1]]; // Pick first 2 after shuffle
};

const getCurrentDateInfo = () => {
  const now = new Date();
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  
  return {
    dayOfWeek: days[now.getDay()],
    month: months[now.getMonth()],
    day: now.getDate(),
    year: now.getFullYear(),
    formatted: `${days[now.getDay()]}, ${months[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`
  };
};

const determineGuestSpeaker = (startingHosts) => {
  // ✅ CRITICAL: Ensure startingHosts is an array
  if (!Array.isArray(startingHosts) || startingHosts.length === 0) {
    console.error('❌ Invalid startingHosts:', startingHosts);
    startingHosts = ["Host 1", "Host 2"]; // Fallback to default
  }
  
  const allHosts = ["Host 1", "Host 2", "Host 3", "Host 4"];
  const availableGuests = allHosts.filter(host => !startingHosts.includes(host));
  
  const randomGuest = availableGuests[Math.floor(Math.random() * availableGuests.length)];
  
  return {
    startingHosts: startingHosts,
    guestSpeaker: randomGuest || "Host 3"
  };
};

const parseDialogueJSON = (content) => {
  try {
    let cleaned = content.trim();
    
    // Remove code block markers
    if (cleaned.startsWith('```json')) {
      cleaned = cleaned.replace(/```json\s*/g, '').replace(/```\s*$/g, '');
    } else if (cleaned.startsWith('```')) {
      cleaned = cleaned.replace(/```\s*/g, '');
    }
    
    cleaned = cleaned.trim();
    
    // ✅ FIX: Convert JavaScript-style escaped quotes to JSON-style
    // Replace \' with ' (unescaped) since single quotes don't need escaping in JSON
    cleaned = cleaned.replace(/\\'/g, "'");
    
    // ✅ NEW: Check if JSON is complete (must end with closing bracket)
    if (!cleaned.endsWith(']')) {
      console.warn('⚠️ JSON appears truncated (no closing bracket)');
      
      // Try to find the last complete dialogue entry
      const lastCompleteEntry = cleaned.lastIndexOf('},');
      if (lastCompleteEntry !== -1) {
        // Cut at last complete entry and add closing bracket
        cleaned = cleaned.substring(0, lastCompleteEntry + 1) + '\n]';
        console.log('✅ Attempted to recover truncated JSON');
      } else {
        throw new Error('JSON is severely truncated - cannot recover');
      }
    }
    
    // ✅ NEW: Additional validation - count opening vs closing brackets
    const openBrackets = (cleaned.match(/\[/g) || []).length;
    const closeBrackets = (cleaned.match(/\]/g) || []).length;
    const openBraces = (cleaned.match(/\{/g) || []).length;
    const closeBraces = (cleaned.match(/\}/g) || []).length;
    
    if (openBrackets !== closeBrackets || openBraces !== closeBraces) {
      console.warn(`⚠️ Bracket mismatch: [${openBrackets}/${closeBrackets}] {${openBraces}/${closeBraces}}`);
      throw new Error('JSON has mismatched brackets');
    }
    
    return JSON.parse(cleaned);
  } catch (error) {
    console.error('Failed to parse dialogue JSON:', error);
    console.error('Content was:', content.substring(0, 500));
    throw new Error('Invalid dialogue JSON format: ' + error.message);
  }
};

const formatNumberForSpeech = (text) => {
  if (!text) return text;
  
  // Pattern: Match currency amounts with commas/decimals
  const currencyPattern = /\$?([\d,]+\.?\d*)\s*(billion|million|thousand)?/gi;
  
  return text.replace(currencyPattern, (match, number, unit) => {
    // Remove commas and parse
    const numValue = parseFloat(number.replace(/,/g, ''));
    
    if (isNaN(numValue)) return match;
    
    // Determine magnitude
    let magnitude = '';
    let displayValue = numValue;
    
    if (numValue >= 1_000_000_000) {
      magnitude = 'billion';
      displayValue = (numValue / 1_000_000_000).toFixed(1).replace('.0', '');
    } else if (numValue >= 1_000_000) {
      magnitude = 'million';
      displayValue = (numValue / 1_000_000).toFixed(1).replace('.0', '');
    } else if (numValue >= 1_000) {
      magnitude = 'thousand';
      displayValue = (numValue / 1_000).toFixed(1).replace('.0', '');
    }
    
    // Use existing unit if provided and larger
    if (unit && unit.toLowerCase() === 'billion' && magnitude !== 'billion') {
      magnitude = 'billion';
    } else if (unit && unit.toLowerCase() === 'million' && magnitude !== 'billion') {
      magnitude = 'million';
    }
    
    // Check if dollar sign present
    const hasDollarSign = match.includes('$');
    
    if (magnitude) {
      return hasDollarSign 
        ? `${displayValue} ${magnitude} dollars`
        : `${displayValue} ${magnitude}`;
    }
    
    return hasDollarSign ? `${displayValue} dollars` : displayValue.toString();
  });
};

/**
 * Apply number formatting to dialogue before generating audio
 */
const formatDialogueNumbersForSpeech = (dialogue) => {
  if (!Array.isArray(dialogue)) return dialogue;
  
  return dialogue.map(line => ({
    ...line,
    line: formatNumberForSpeech(line.line)
  }));
};

const NATURAL_SPEECH_GUIDELINES = `
NATURAL SPEECH PATTERNS - CRITICAL FOR AUTHENTICITY:
⚠️ CRITICAL: USE FILLERS SPARINGLY - NOT IN EVERY SENTENCE!
Real conversations have fillers, but NOT constantly. Most sentences should be clear and direct.
Think: 1 filler word per every 8-10 sentences, not per sentence!

1. FILLER WORDS & HESITATIONS (Use RARELY and NATURALLY - only 10-15% of lines):
   ✅ Use these occasionally when they feel natural:
   - "like" (most common): "It's like, really interesting" or "Like, I was thinking..."
   - "you know": "You know what I mean?" or "It's, you know, complicated"
   - "I mean": "I mean, honestly..." or "It's just, I mean, fascinating"
   - "so": "So basically..." or "So here's the thing..."
   - "um" / "uh": "Um, I think..." or "Uh, that's a good point"
   - "kind of" / "sort of": "It's kind of wild" or "Sort of makes sense"
   - "actually": "Actually, that reminds me..." or "It's actually pretty cool"
   - "right": "Right?" or "That's right"
   - "well": "Well, here's what I think..." or "Well, actually..."
   
⚠️ CRITICAL: Don't use fillers in most lines! Only add them occasionally for realism.
   Most lines should be filler-free. When you do use them, spread naturally across conversation.
      ✅ Good: "So, like, I was reading about this and, um, it's pretty wild"
   ❌ Too much: "So, like, um, you know, I mean, well, actually..."

2. INCOMPLETE THOUGHTS & INTERRUPTIONS:
   - Hosts can trail off: "It's just that... I don't know, maybe—"
   - Another host jumps in: "Right! Exactly what I was thinking!"
   - Self-corrections: "It's about 50— wait, no, closer to 60 degrees"
   - Restatements: "The temperature is... well, it's pretty cold out there"

3. BACKGROUND REACTIONS (CRITICAL - Use 15-20 times per podcast):
   ⚠️ THIS IS ESSENTIAL FOR NATURAL FLOW
   
   Add "background": true for SHORT reactions that happen WHILE another speaker talks:
   
   ✅ Use any of this Examples of background reactions:
   - "mhm" (agreement while listening)
   - "Yeah" (acknowledging)
   - "Right" (understanding)
   - "Oh woah" (surprise)
   - "Interesting" (engaged listening)
   - "For sure" (agreement)
   - "Totally" (strong agreement)
   - "Hmm" (thinking)
   - "Oh really" (curiosity)
   - "No way" (disbelief)
   
   ⚠️ Background reactions must be:
   - 1-3 words maximum
   - Happen DURING another speaker's line (not separate turns)
   - Used 15-20 times throughout the podcast
   - Varied (don't repeat the same reaction too often)
   
   ✅ Proper format:
   {
     "speaker": "Host 2",
     "line": "Mhm",
     "timestamp": 15,
     "keywords": [],
     "background": true
   }
   
   Place these BETWEEN regular dialogue to show active listening!

4. CONVERSATIONAL PATTERNS:
   - Start with "So..." or "Okay so..." frequently
   - Use "I think" or "I feel like" to soften statements
   - Add "honestly" or "to be honest" for emphasis
   - Use "basically" when summarizing
   - End with "you know?" or "right?" to engage

5. NATURAL TRANSITIONS:
   ❌ Avoid: "Moving on to the next topic..."
   ✅ Use: "Oh! Speaking of that..." or "That reminds me..." or "Actually..."

6. REALISTIC REACTIONS:
   - "Wait, what?" (confusion)
   - "Hold on, hold on" (need to interject)
   - "Okay okay, but..." (gentle disagreement)
   - "That's wild" (surprise)
   - "No way!" (disbelief)
   - "For real?" (skepticism)

FREQUENCY GUIDELINES:
- Filler words: Use sparingly in 10-15% of lines maximum (not every sentence!)
- Background reactions: 15-20 throughout entire podcast (THIS IS CRITICAL)
- Incomplete thoughts: 5-8 times per podcast
- "Like" specifically: Use in 8-12% of lines only when it sounds natural
- Most sentences should be clean and clear without fillers

EXAMPLE OF NATURAL DIALOGUE WITH FILLERS & BACKGROUND REACTIONS:

[
  {
    "speaker": "Host 1",
    "line": "Hey there, welcome back to the InnerLoop! So, like, we've got some interesting stuff to talk about today.",
    "timestamp": 0,
    "keywords": [],
    "section": "Introduction"
  },
  {
    "speaker": "Host 2",
    "line": "Yeah",
    "timestamp": 4,
    "keywords": [],
    "background": true
  },
  {
    "speaker": "Host 2",
    "line": "For sure! I mean, the weather today is, um, kind of crazy. It's like 52 degrees but it feels colder, you know?",
    "timestamp": 5,
    "keywords": ["52 degrees"]
  },
  {
    "speaker": "Host 1",
    "line": "Right",
    "timestamp": 10,
    "keywords": [],
    "background": true
  },
  {
    "speaker": "Host 1",
    "line": "Right? And I was checking the forecast earlier and it's actually supposed to drop to, like, 45 tonight. So, yeah, bring a jacket.",
    "timestamp": 11,
    "keywords": ["45"]
  },
  {
    "speaker": "Host 2",
    "line": "Oh wow",
    "timestamp": 16,
    "keywords": [],
    "background": true
  },
  {
    "speaker": "Host 2",
    "line": "Okay so, speaking of cold weather, there's this whole thing about traffic today. Um, apparently there's a pretty big delay on Route 3.",
    "timestamp": 17,
    "keywords": ["Route 3"]
  },
  {
    "speaker": "Host 1",
    "line": "Mhm",
    "timestamp": 22,
    "keywords": [],
    "background": true
  },
  {
    "speaker": "Host 1",
    "line": "Oh no, really? Like, how bad are we talking?",
    "timestamp": 23,
    "keywords": []
  },
  {
    "speaker": "Host 2",
    "line": "Yeah, so it's like, I mean, about an 8-minute delay. Which, you know, doesn't sound terrible but if you're already running late... it's kind of a pain.",
    "timestamp": 26,
    "keywords": ["8-minute"]
  },
  {
    "speaker": "Host 1",
    "line": "Totally",
    "timestamp": 32,
    "keywords": [],
    "background": true
  },
  {
    "speaker": "Host 1",
    "line": "For sure. Actually, I think there's an alternate route through, um, Bloomfield that could save some time.",
    "timestamp": 33,
    "keywords": ["Bloomfield"]
  },
  {
    "speaker": "Host 2",
    "line": "Oh really",
    "timestamp": 38,
    "keywords": [],
    "background": true
  },
  {
    "speaker": "Host 2",
    "line": "Oh interesting! So like, if you take that route, you're basically avoiding all the heavy traffic, right?",
    "timestamp": 39,
    "keywords": []
  },
  {
    "speaker": "Host 1",
    "line": "Exactly",
    "timestamp": 43,
    "keywords": [],
    "background": true
  },
  {
    "speaker": "Host 1",
    "line": "Exactly! I mean, it's, you know, a little bit longer distance-wise but you'll save like 5 minutes overall. Which, honestly, is worth it.",
    "timestamp": 44,
    "keywords": ["5 minutes"]
  }
]

⚠️ CRITICAL REMINDERS:
1. Background reactions MUST be used 15-20 times (they're currently almost never used!)
2. Filler words like   "like",
  "um",
  "uh",
  "you know",
  "I mean",
  "so",
  "actually",
  "basically",
  "literally",
  "kinda",
  "sorta",
  "well",
  "right",
  "you see",
  "okay",
  "hmm",
  "yeah",
  "I guess",
  "to be honest",
  "you get me",
  "I suppose",
  "honestly",
  "anyway",
  "so yeah",
  "you know what I mean",
  "uhh",
  "huh",
  "alright",
  "let’s see",
  "mmm"
  should appear in 20-30% of lines
3. This is how real people talk - embrace the messiness!
4. Background reactions show active listening and make it feel like a real conversation
`;
const NUMBER_PRONUNCIATION_GUIDELINES = `
=== NUMBER PRONUNCIATION - CORRECT FORMAT ===

When discussing large numbers, format them for natural speech:

✅ CORRECT:
- "$10,000,000,000" → "10 billion dollars"
- "$5.2 billion" → "5.2 billion dollars"  
- "$850 million" → "850 million dollars"
- "2,500 employees" → "2,500 employees" (leave as-is)

❌ INCORRECT:
- "10 thousand million dollars" (too complicated)
- "ten thousand billion dollars" (mathematically wrong)
- "10,000,000,000 dollars" (too many digits)

CURRENCY RULES:
- Billions: $1,000,000,000+ → "X billion dollars"
- Millions: $1,000,000+ → "X million dollars"
- Thousands: $1,000+ → "X thousand dollars"
- Round to 1 decimal if needed: "$5.2 billion"

NON-CURRENCY LARGE NUMBERS:
- "10,000 users" → "10 thousand users"
- "2.5 million views" → "2.5 million views"
- "Population: 8,000,000" → "Population: 8 million"

Keep it simple and conversational!
`;

// Add this to your NATURAL_SPEECH_GUIDELINES export
export const ENHANCED_SPEECH_GUIDELINES = `
${NATURAL_SPEECH_GUIDELINES}

${NUMBER_PRONUNCIATION_GUIDELINES}
`;


const STOCK_DISCUSSION_GUIDELINES = `
=== STOCK MARKET DISCUSSION - CONVERSATIONAL STYLE ===

When discussing stocks, sound like a friend explaining to another friend, NOT a financial analyst.

✅ DO SAY (Natural, Simple):
- "Apple is up 2% today"
- "Tesla took a hit, down 3%"
- "The S&P 500 is pretty much flat"
- "NVIDIA is having a great day"
- "Markets are looking good/rough/mixed today"
- "That's a pretty big move"
- "It's trading near its high for the year"
- "Lots of people are buying/selling today"

❌ DON'T SAY (Too Technical):
- "AAPL is exhibiting bullish momentum"
- "The equity is experiencing significant volatility"
- "There's been a retracement to support levels"
- "The technicals suggest consolidation"
- "Volume indicators are showing divergence"

🎯 EXPLAIN WHY IT MATTERS (when relevant):
- "Tesla's down 3%—that's pretty significant for a single day"
- "Apple hit its 52-week high, which means it's as high as it's been all year"
- "Trading volume is way up—means something's got investors interested"
- "The S&P 500 represents the overall market, so when it's up, most stocks are doing well"

💬 USE EVERYDAY COMPARISONS:
- Instead of: "The stock appreciated 15%"
  Say: "It jumped 15%—that's a pretty solid gain"
  
- Instead of: "Experiencing downward pressure"
  Say: "Taking a hit" or "Dropping" or "Selling off"
  
- Instead of: "Consolidating in a range"
  Say: "Bouncing around" or "Pretty much flat"

🚫 AVOID JARGON:
- NO: resistance, support, consolidation, retracement, divergence
- YES: high, low, up, down, flat, moving, jumping, dropping

Keep it simple, conversational, and explain context when needed!
`;
// 7. UPDATE WEB SEARCH INTEGRATION in dialogue
// Add to your system prompt where you discuss sections:

const WEB_SEARCH_INTEGRATION = `
=== REAL-TIME WEB SEARCH ANSWERS ===

When a web search answer is included in the content:
- Introduce it naturally: "So I looked this up..." or "According to..."
- Cite the source casually: "Based on what [Source] is reporting..."
- Acknowledge if it's breaking/recent: "This just came out..." or "As of today..."
- Don't overemphasize—just deliver the info naturally

Example:
Host 1: "When's the next wrestling event this weekend?"
Host 2: "So I checked—according to WWE's schedule, they've got an event Saturday night at 8pm in Newark."
Host 1: "Perfect, that's not too far."

DON'T make it sound robotic or overly formal. Keep it conversational!
`;
const TALKING_STYLE_GUIDELINES = {
  casual: `
=== CASUAL TALKING STYLE ===
This is the default, friendly conversational style.

- Use natural fillers: "like", "you know", "I mean", "so"
- Relaxed, conversational tone
- Background reactions: "Yeah", "Mhm", "Right", "For sure"
- Occasional incomplete thoughts
- Friendly interruptions
- Example: "So, like, the weather today is pretty wild, you know?"
`,
  
  'semi-formal': `
=== SEMI-FORMAL TALKING STYLE ===
Professional but still conversational - like NPR or business podcasts.

- Minimal fillers (reduce by 70%)
- Complete sentences more often
- Fewer background reactions (keep only 5-8 per podcast)
- More structured delivery
- Still warm and engaging, just more polished
- Example: "The weather today is quite interesting. Let me explain why."

KEY DIFFERENCES:
- Replace "like" with pauses or removal
- Replace "you know" with clear statements
- Fewer "um" and "uh" moments
- More deliberate pacing
- Still personable, not robotic
`
};
/**
 * Generate podcast dialogue with 4 hosts (2 start, 1 guest joins)
 */
const generatePodcastDialogue = async (
  topic, 
  searchResults = null, 
  includeGuest = false, 
  startingHosts = ["Host 1", "Host 2"],
  userName = null,
  AZURE_OPENAI_ENDPOINT,
  AZURE_OPENAI_DEPLOYMENT,
  AZURE_OPENAI_API_KEY,
  talkingStyle = 'casual'  // ✅ ADD THIS LINE
) => {
const maxRetries = 2;
let lastError;
const getTalkingStylePrompt = (style) => {
  return TALKING_STYLE_GUIDELINES[style] || TALKING_STYLE_GUIDELINES.casual;
};

// ✅ CRITICAL: Ensure startingHosts is an array before passing
const validStartingHosts = Array.isArray(startingHosts) && startingHosts.length >= 2 
  ? startingHosts 
  : ["Host 1", "Host 2"];

const speakerConfig = determineGuestSpeaker(validStartingHosts);
const guestSpeaker = speakerConfig.guestSpeaker;
  
  // Get time context with personalization
  const timeOfDay = getTimeOfDay();
  const greeting = getTimeGreeting();
  const dateInfo = getCurrentDateInfo();
  
  console.log('\n📋 Podcast Context:');
  console.log(`   Date: ${dateInfo.formatted}`);
  console.log(`   Time: ${timeOfDay}`);
  console.log(`   Greeting: "${greeting}"`);
  console.log(`   User Name: ${userName || 'Anonymous'}`);
  console.log(`   Guest Feature: ${includeGuest ? 'ENABLED ✓' : 'DISABLED ✗'}`);
  
  let timeContext = `\n\n=== CURRENT CONTEXT ===\n`;
  timeContext += `Today is: ${dateInfo.formatted}\n`;
  timeContext += `Time of day: ${timeOfDay}\n`;
  timeContext += `Appropriate greeting: "${greeting}"\n`;
  
if (userName) {
    timeContext += `\n=== PERSONALIZATION ===\n`;
    timeContext += `User's name: ${userName}\n`;
    timeContext += `THIS IS THEIR PERSONAL PODCAST - speak directly to them.\n`;
    timeContext += `Use "you" and "your" throughout, not "everyone" or "listeners"\n`;
    timeContext += `Listener's name: ${userName}\n`;
    timeContext += `CRITICAL: In the very first exchange, greet them by name naturally:\n`;
    timeContext += `- If morning: "Good morning ${userName}, welcome back." or "Hey ${userName}, good morning."\n`;
    timeContext += `- If afternoon: "Hey ${userName}, good afternoon." or "Welcome back ${userName}."\n`;
    timeContext += `- If evening: "Good evening ${userName}." or "Hey ${userName}, welcome back."\n`;
    timeContext += `- If night: "Hey ${userName}, thanks for joining us." or "Welcome ${userName}."\n`;
    timeContext += `After the first greeting, use their name sparingly (maybe 1-2 more times throughout)\n\n`;
  }
  
  timeContext += `GREETING GUIDELINES:\n`;
  if (timeOfDay === 'morning') {
    timeContext += `- It's morning, natural intros: ${userName ? `"Hey ${userName}, good morning"` : '"Hey, good morning"'}\n`;
    timeContext += `- Energy should be upbeat but not overly energetic\n`;
    timeContext += `- NO personal life stories (no "today I did X" or "this morning I")\n`;
  } else if (timeOfDay === 'afternoon') {
    timeContext += `- It's afternoon, casual intros: ${userName ? `"Hey ${userName}"` : '"Hey"'} or just jump to topic\n`;
    timeContext += `- Steady, focused energy\n`;
    timeContext += `- NO personal life stories (no "today I did X" or "earlier I")\n`;
  } else if (timeOfDay === 'evening') {
    timeContext += `- It's evening, warm intros: ${userName ? `"Hey ${userName}, welcome back"` : '"Hey, welcome back"'}\n`;
    timeContext += `- Relaxed, conversational tone\n`;
    timeContext += `- NO personal life stories (no "today I did X" or "this evening I")\n`;
  } else {
    timeContext += `- It's nighttime, casual intros: ${userName ? `"Hey ${userName}"` : '"Hey"'} or "Welcome"\n`;
    timeContext += `- Calm, focused tone\n`;
    timeContext += `- NO personal life stories (no "today I did X" or "tonight I")\n`;
  }
  
  let searchContext = '';
  if (searchResults && searchResults.length > 0) {
    searchContext += `\n\n=== NEWS SOURCES (Use these facts in dialogue) ===\n`;
    searchResults.forEach((result, idx) => {
      searchContext += `\n${idx + 1}. ${result.title}\n`;
      searchContext += `   Source: ${result.source}\n`;
      searchContext += `   Date: ${result.dateWithDayOfWeek}\n`;
      searchContext += `   Summary: ${result.snippet.substring(0, 180)}...\n`;
    });
    searchContext += `\n⚠️ CRITICAL: Mention specific dates when discussing each news item.\n`;
    searchContext += `Use the date format from "Date:" field above (e.g., "today Thursday, October 9th" or "yesterday Wednesday, October 8th")\n`;
  }
let guestContext = '';
if (includeGuest) {
  // Helper function to get host display name
  const getHostName = (hostLabel) => {
    const nameMap = {
      'Host 1': 'Emma',
      'Host 2': 'James',
      'Host 3': 'Jackie',
      'Host 4': 'Kenny'
    };
    return nameMap[hostLabel] || hostLabel;
  };

  guestContext += `\n\n=== GUEST SPEAKER FEATURE ===\n`;
  guestContext += `SPEAKER CONFIGURATION:\n`;
  guestContext += `- Starting hosts: ${speakerConfig.startingHosts[0]} and ${speakerConfig.startingHosts[1]}\n`;
  guestContext += `- Guest speaker who joins: ${guestSpeaker}\n\n`;
  
  guestContext += `⚠️ CRITICAL: VARY THE GUEST ENTRANCE STYLE\n`;
  guestContext += `Pick ONE style randomly from the options below. Each podcast should feel different!\n\n`;
  
  guestContext += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  guestContext += `STYLE 1: CASUAL DROP-IN (30% of the time)\n`;
  guestContext += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  guestContext += `Guest shows up naturally, no big announcement.\n\n`;
  
  guestContext += `Option A - Curiosity entrance:\n`;
  guestContext += `Host 1: "This is getting complicated. We need someone who actually knows this stuff."\n`;
  guestContext += `Host 2: "Well, ${getHostName(guestSpeaker)} was researching this yesterday."\n`;
  guestContext += `${guestSpeaker}: "Did someone mention [topic word]? Because I've been diving deep into this."\n`;
  guestContext += `Host 1: "Perfect timing. Tell us what we're missing."\n\n`;
  
  guestContext += `Option B - Overhearing entrance:\n`;
  guestContext += `${guestSpeaker}: "Sorry, I couldn't help overhearing—you're totally on the right track here."\n`;
  guestContext += `Host 2: "Oh! ${getHostName(guestSpeaker)}, yes! We're so confused."\n`;
  guestContext += `Host 1: "Save us. What's really going on?"\n\n`;
  
  guestContext += `Option C - Natural segue:\n`;
  guestContext += `Host 1: "You know what, this reminds me of what ${getHostName(guestSpeaker)} was saying earlier."\n`;
  guestContext += `${guestSpeaker}: "Wait, are we talking about [topic]? Because there's something crucial you're both missing."\n`;
  guestContext += `Host 2: "Okay, we're listening. What is it?"\n\n`;
  
  guestContext += `Option D - Excited discovery:\n`;
  guestContext += `${guestSpeaker}: "Okay, I just have to stop you there—this is way more interesting than you think."\n`;
  guestContext += `Host 1: "Alright, ${getHostName(guestSpeaker)}, you've got our attention."\n`;
  guestContext += `${guestSpeaker}: "So here's what most people don't realize..."\n\n`;
  
  guestContext += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  guestContext += `STYLE 2: DIRECT INVITATION (25% of the time)\n`;
  guestContext += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  
  guestContext += `Option A - Expert call:\n`;
  guestContext += `Host 1: "This is above our pay grade. ${getHostName(guestSpeaker)}, you there?"\n`;
  guestContext += `Host 2: "We desperately need your expertise right now."\n`;
  guestContext += `${guestSpeaker}: "Always happy to help. So what's the confusion?"\n\n`;
  
  guestContext += `Option B - Better source:\n`;
  guestContext += `Host 2: "Actually, ${getHostName(guestSpeaker)} knows way more about this than we do."\n`;
  guestContext += `Host 1: "Right! ${getHostName(guestSpeaker)}, come explain this properly."\n`;
  guestContext += `${guestSpeaker}: "Absolutely. The key thing to understand here is..."\n\n`;
  
  guestContext += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  guestContext += `STYLE 3: SURPRISE/INTERRUPT (20% of the time)\n`;
  guestContext += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  
  guestContext += `Option A - Breaking news:\n`;
  guestContext += `Host 1: "So we're thinking this might affect the market by—"\n`;
  guestContext += `${guestSpeaker}: "Hold on! Did you see what just dropped? This changes everything."\n`;
  guestContext += `Host 2: "Wait, what? ${getHostName(guestSpeaker)}, what happened?"\n\n`;
  
  guestContext += `Option B - Correction entrance:\n`;
  guestContext += `${guestSpeaker}: "Actually, actually—I have to stop you right there."\n`;
  guestContext += `Host 1: "Uh oh. What did we get wrong?"\n`;
  guestContext += `${guestSpeaker}: "Not wrong, just incomplete. Here's the piece you're missing..."\n\n`;
  
  guestContext += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  guestContext += `STYLE 4: PRE-ANNOUNCED (10% of the time)\n`;
  guestContext += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  
  guestContext += `Hosts mention guest early, bring them in later.\n\n`;
  guestContext += `Host 1: "This is going to get technical. We might need ${getHostName(guestSpeaker)} for this one."\n`;
  guestContext += `Host 2: "Good thinking. Let's start with the basics..."\n`;
  guestContext += `[... 6-8 exchanges later ...]\n`;
  guestContext += `Host 1: "Okay, this is getting complex. ${getHostName(guestSpeaker)}, you ready?"\n`;
  guestContext += `${guestSpeaker}: "Absolutely. So the technical side of this is fascinating..."\n\n`;
  
  guestContext += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  guestContext += `STYLE 5: TRADITIONAL GUEST INTRO (15% of the time)\n`;
  guestContext += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  guestContext += `Guest is announced early, gives brief thanks, jumps straight in.\n\n`;
  
  guestContext += `Option A - Direct intro with momentum:\n`;
  guestContext += `Host 1: "So we actually have a guest on today's show. ${getHostName(guestSpeaker)} is joining us."\n`;
  guestContext += `${guestSpeaker}: "Thanks for having me! So let me get right into this..."\n`;
  guestContext += `Host 2: "Perfect. We're all ears."\n\n`;
  
  guestContext += `Option B - Casual welcome:\n`;
  guestContext += `Host 2: "We've got ${getHostName(guestSpeaker)} with us today to break this down."\n`;
  guestContext += `${guestSpeaker}: "Hey! Thanks. Okay, so here's what everyone needs to understand..."\n`;
  guestContext += `Host 1: "Love it. Keep going."\n\n`;
  
  guestContext += `Option C - Topic-first intro:\n`;
  guestContext += `Host 1: "For today's topic, we brought in ${getHostName(guestSpeaker)}."\n`;
  guestContext += `${guestSpeaker}: "Appreciate it! So the first thing you need to know is..."\n`;
  guestContext += `Host 2: "This is exactly what we needed."\n\n`;
  
  guestContext += `Option D - Expert introduction:\n`;
  guestContext += `Host 2: "We have ${getHostName(guestSpeaker)} here today, who's been following this closely."\n`;
  guestContext += `${guestSpeaker}: "Thanks! Happy to be here. Let's dive in—"\n`;
  guestContext += `Host 1: "Yes, let's do it."\n\n`;
  
  guestContext += `CRITICAL FOR THIS STYLE:\n`;
  guestContext += `- Guest thanks briefly (1-3 words max: "Thanks!", "Appreciate it!", "Hey thanks!")\n`;
  guestContext += `- Immediately continues with content (no pausing or waiting)\n`;
  guestContext += `- Hosts keep it moving, don't slow down for formalities\n`;
  guestContext += `- Energy stays high and conversational\n\n`;
  
  guestContext += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  guestContext += `STYLE 6: COLLABORATIVE REVEAL (10% of the time)\n`;
  guestContext += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  
  guestContext += `Host 2: "There's something bigger here we're not seeing."\n`;
  guestContext += `${guestSpeaker}: "That's exactly what I was thinking. Can I add something?"\n`;
  guestContext += `Host 1: "Please! What are we missing?"\n`;
  guestContext += `${guestSpeaker}: "So if you connect these dots..."\n\n`;
  
  guestContext += `⚠️ FORBIDDEN PHRASES - NEVER USE THESE:\n`;
  guestContext += `❌ "I need to jump in here" (overused!)\n`;
  guestContext += `❌ "I have to interrupt" (too formal)\n`;
  guestContext += `❌ "I must interject" (too stiff)\n`;
  guestContext += `❌ "Allow me to explain" (sounds scripted)\n`;
  guestContext += `❌ "Let me tell you" (patronizing)\n\n`;
  
  guestContext += `✅ INSTEAD USE (vary constantly):\n`;
  guestContext += `- "Hold on—" / "Wait—" / "Okay—"\n`;
  guestContext += `- "Actually..." / "So..." / "Here's the thing..."\n`;
  guestContext += `- "Oh!" / "Ah!" / "Right!"\n`;
  guestContext += `- "Can I add something?" / "Quick thing..."\n`;
  guestContext += `- "That reminds me..." / "Speaking of that..."\n`;
  guestContext += `- "Okay so..." / "Listen..." / "Check this out..."\n\n`;
  
  guestContext += `TIMING FOR GUEST ENTRANCE:\n`;
  guestContext += `- Guest joins around exchange 8-12 (30-50 seconds in)\n`;
  guestContext += `- NOT at beginning (hosts set up topic first)\n`;
  guestContext += `- NOT at end (guest needs time to contribute)\n\n`;
  
  guestContext += `GUEST PARTICIPATION:\n`;
  guestContext += `- Guest speaks 30-40% after entering\n`;
  guestContext += `- Brings expertise/new perspective\n`;
  guestContext += `- Can disagree: "I see it differently..." / "Here's another angle..."\n`;
  guestContext += `- All three build off each other\n\n`;
  
  guestContext += `CRITICAL REMINDERS:\n`;
  guestContext += `- Each podcast feels DIFFERENT—vary entrance style!\n`;
  guestContext += `- NO repetitive phrases like "I need to jump in here"\n`;
  guestContext += `- Guest is ${guestSpeaker} (${getHostName(guestSpeaker)})\n`;
  guestContext += `- Only use: "${speakerConfig.startingHosts[0]}", "${speakerConfig.startingHosts[1]}", "${guestSpeaker}"\n`;
  guestContext += `- Make it feel spontaneous, like real experts who like each other\n\n`;
}
const validSpeakers = includeGuest 
  ? [validStartingHosts[0], validStartingHosts[1], guestSpeaker]
  : validStartingHosts;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      console.log(`🎙️ Podcast generation attempt ${attempt + 1}/${maxRetries}`);
      
     const data = await SecureAPI.callAzureOpenAI([
        {
          role: 'system',
content: `You are a podcast script writer. Create a personalized conversation between ${validSpeakers.join(', ')}${includeGuest ? ` (where ${guestSpeaker} joins as a guest)` : ''} that speaks DIRECTLY to the listener (${userName || 'the user'}).
⚠️⚠️⚠️ CRITICAL INSTRUCTION ⚠️⚠️⚠️
THE USER PROVIDED DOCUMENT CONTENT BELOW. YOU MUST CREATE A PODCAST THAT DISCUSSES THIS SPECIFIC CONTENT.
NOT A GENERIC PODCAST ABOUT SUMMARIZATION, BUT ABOUT THE ACTUAL DOCUMENT THEY PROVIDED.

${topic}

YOUR JOB: Create a natural, engaging podcast dialogue where the hosts discuss the SPECIFIC CONTENT from this document. They should:
- Reference actual topics, concepts, and details from the document
- Ask questions about the content
- Debate or discuss different aspects
- Highlight key points the user mentioned
- Make it conversational and educational
- Sound like they've read/analyzed this specific material

⚠️⚠️⚠️ CRITICAL AUDIENCE RULES ⚠️⚠️⚠️
1. The hosts are talking TO the user, not to a general audience
2. Say "you" not "everyone" or "listeners"
3. NEVER say: "for everyone listening", "if you're out there"
4. ALWAYS say: "for you", "you might want to", "this affects you because"

Examples:
❌ BAD: "So for everyone listening today, diploblasts..."
✅ GOOD: "So for you, diploblasts are important because..."

⚠️⚠️⚠️ HOST BEHAVIOR RULES ⚠️⚠️⚠️
1. Hosts MUST discuss the ACTUAL DOCUMENT CONTENT
2. Hosts should NOT talk about generic summarization techniques
3. Focus on explaining and discussing the material from the document
4. Make connections between concepts in the document
5. Discuss why points matter for the user's studying/learning

⚠️⚠️⚠️ CRITICAL AUDIENCE RULES ⚠️⚠️⚠️
1. The hosts are talking TO the user, not to a general audience
2. Say "you" not "everyone" or "listeners" or "people"
3. NEVER say: "for everyone listening", "if you're out there", "for those tuning in"
4. ALWAYS say: "for you", "you might want to", "this affects you because"
5. Make it feel like a personal briefing made just for them

Examples:
❌ BAD: "So for everyone listening today, the weather is..."
✅ GOOD: "So for you today, the weather is..."

❌ BAD: "If you're heading out, listeners..."
✅ GOOD: "If you're heading out..."

❌ BAD: "Thanks for tuning in everyone!"
✅ GOOD: "That's your update for today!"

⚠️⚠️⚠️ HOST BEHAVIOR RULES ⚠️⚠️⚠️
1. Hosts CANNOT ask each other personal experience questions
2. Hosts are AI presenters, NOT real people with lives
3. Focus on delivering information to the USER, not chatting with each other

❌ FORBIDDEN - Hosts asking each other about their experiences:
- "Do you ever take melatonin?"
- "Have you tried this route?"
- "What do you think about..."
- "Do you remember when..."
- "Have you noticed..."

✅ CORRECT - Hosts discussing information for the user:
- "You might want to try melatonin if..."
- "This route could save you time if..."
- "Here's what you need to know about..."
- "This matters because..."
- "You should be aware that..."

HOSTS DISCUSS THE TOPIC, NOT THEIR OPINIONS OR EXPERIENCES!

⚠️⚠️⚠️ DISCUSSING CURRENT INFORMATION ⚠️⚠️⚠️

When discussing information provided to you:

1. **Speak naturally without citing sources**
   - Present information as if you know it personally
   - Don't say "According to..." or "Based on reports..."
   - Just discuss the facts conversationally
   
   ✅ GOOD: "Tesla's stock jumped 5% today."
   ❌ BAD: "According to Yahoo News, Tesla's stock jumped 5%."

2. **You can mention it's current/recent if relevant:**
   - "The latest is that..."
   - "What's happening now is..."
   - "Right now..."
   
3. **Focus on WHAT matters, not WHERE you learned it**

When discussing information from web searches or news:

1. **NEVER repeatedly mention dates or sources**
   - Mention the source ONCE at the beginning if needed for credibility
   - Then speak naturally as if you personally know this information
   
2. **AVOID repetitive phrases like:**
   ❌ "According to Yahoo News from December 18th..."
   ❌ "CNN reported on December 18th that..."
   ❌ "A December 18th article from Reuters states..."
   ❌ "Based on reporting from December 18th..."
   
3. **USE natural language instead:**
   ✅ "So I was reading about this earlier, and it turns out..."
   ✅ "Recent reports show that..."
   ✅ "Here's what's interesting..."
   ✅ "From what I've found..."
   ✅ "The latest is that..."
   
4. **For news discussions:**
   - First mention: "According to [Source], [fact]"
   - After that: Just discuss the facts naturally
   - Example: "According to Reuters, the market jumped 3%. This is because investors are optimistic about the new policy."
   
5. **Sound like informed hosts, not news anchors reading citations**
   - You're having a conversation, not writing a research paper
   - After initial attribution, discuss information as if you know it
   - Focus on WHY it matters, not WHERE you read it

GOOD EXAMPLE:
Host 1: "So according to Reuters, Tesla's stock jumped 5% today."
Host 2: "Yeah, that's a pretty big move. It seems like investors are excited about the new model announcement."
Host 1: "Right, and this could push other EV makers to react."

BAD EXAMPLE:
Host 1: "According to Reuters from December 18th, Tesla's stock jumped."
Host 2: "And Yahoo Finance also reported on December 18th that..."
Host 1: "Based on a December 18th CNN article..."

${timeContext}${searchContext}${guestContext}

=== TRAFFIC DELIVERY RULES ===
When discussing traffic:
1. Be DIRECT and CLEAR - no storytelling or personal anecdotes
2. Give the essential info: route, delay time, cause (if known), alternative
3. Don't waste time with "oh I remember when..." or "you know what's funny..."
4. Format: "Route X has an 8-minute delay due to [cause]. You can avoid it by taking [alternative]."

**NUMBER PRONUNCIATION - CRITICAL:**
When speaking numbers, hosts must say them naturally:

✅ CORRECT:
- $10,000,000 → "ten million dollars"
- $5.2 billion → "five point two billion dollars"
- $1,500,000 → "one point five million dollars" or "a million and a half dollars"
- 2.5% → "two point five percent"
- $485.67 → "four hundred eighty-five dollars"
- 15,000 → "fifteen thousand"

❌ WRONG:
- $10,000,000 → "ten zero zero zero zero zero zero dollars" or "one zero million dollars"
- $5.2 billion → "five point two thousand million"
- Writing out "10,000,000" expecting TTS to say it correctly

**WRITE NUMBERS AS WORDS:**
- Always spell out numbers in dialogue
- TTS reads written numbers better: "ten million" not "$10M" or "10000000"
- Use words for clarity: "five percent" not "5%"

❌ BAD Traffic Discussion:
Host 1: "Oh man, Route 3 is always crazy, right?"
Host 2: "Yeah, I remember one time I was stuck there for like an hour..."
Host 1: "So relatable! Anyway, there's a delay today..."

✅ GOOD Traffic Discussion:
Host 1: "Route 3 has an 8-minute delay right now due to an accident near Exit 9."
Host 2: "Your best alternative is taking Bloomfield Avenue - that'll save you about 5 minutes."
Host 1: "Exactly. So if you're heading that way, reroute now."

Keep traffic updates TIGHT and ACTIONABLE!

${NATURAL_SPEECH_GUIDELINES}
${STOCK_DISCUSSION_GUIDELINES}  

// ADD THESE LINES RIGHT AFTER STOCK_DISCUSSION_GUIDELINES:
${talkingStyle === 'casual' 
  ? TALKING_STYLE_GUIDELINES.casual 
  : TALKING_STYLE_GUIDELINES['semi-formal']}

${NUMBER_PRONUNCIATION_GUIDELINES}

⚠️ CRITICAL NUMBER FORMATTING:
- $10,000,000,000 → "10 billion dollars"
- $5.2 billion → "5.2 billion dollars"
- Never say "thousand billion" or "thousand million"

${WEB_SEARCH_INTEGRATION}  
⚠️⚠️⚠️ YOUTUBE VIDEO DISCUSSION RULES ⚠️⚠️⚠️
When discussing a YouTube video with transcript:

1. **SUMMARIZE, DON'T NARRATE LINE-BY-LINE**
   - Discuss themes, patterns, and key arguments
   - Group related points together naturally
   - Talk about the VIDEO'S MESSAGE, not individual timestamps
   
   ✅ GOOD: "The video makes a strong case that New York's role as a financial hub is at risk. Ackman argues that political shifts could have global ripple effects. Then they pivot to AI, with Amon explaining how it's moving beyond data centers into phones, cars, and industrial equipment. The big takeaway? AI is becoming infrastructure, not just software."
   
   ❌ BAD: "At 1:37, Ackman says New York is important. Then at 3:12, Amon talks about AI. At 4:45, they mention..."

2. **USE VAGUE TIME REFERENCES, NEVER SPECIFIC TIMESTAMPS**
   - ✅ ALLOWED: "early on", "later", "towards the end", "halfway through", "at one point"
   - ❌ FORBIDDEN: "at 1:37", "around 3:12", "at the 7-minute mark", "4:45 into the video"
   - Keep it conversational and natural
   
   ✅ GOOD Examples:
   - "Early on, Ackman calls New York the most important financial capital..."
   - "Later in the discussion, the conversation shifts to AI..."
   - "Towards the end, they debate whether Facebook or Amazon..."
   - "At one point, they throw out this question about..."
   - "Halfway through, Amon compares this to the birth of the internet..."
   
   ❌ BAD Examples:
   - "At 1:37, he says..." (specific timestamp - NO!)
   - "Around 3:12, Amon talks..." (specific time - NO!)
   - "At the 7-minute mark..." (specific time - NO!)
   - "4:45 into the video..." (specific timestamp - NO!)

3. **QUOTE IMPORTANT STATEMENTS (NOT EVERYTHING)**
   - Only quote 2-3 most impactful lines from the entire video
   - Introduce quotes naturally, not with timestamps
   
   ✅ GOOD: "Ackman makes this powerful point when he calls New York 'the most important financial capital in the world' and warns about political risks."
   
   ❌ BAD: "At 1:37, Ackman says, quote, 'the most important financial capital in the world.'"

4. **DISCUSS THEMES AND DEBATES, NOT CHRONOLOGY**
   - Organize by topics/arguments, not by time order
   - Explain WHY these points matter to the listener
   
   ✅ GOOD: "There's this fascinating debate about AI's future. Some see it as just data center stuff, but Amon pushes back hard—he's talking phones, cars, industrial machines. The implication? AI becomes the operating system for everything around you."
   
   ❌ BAD: "First they talk about New York, then AI, then stocks, then..."

5. **FILLER WORDS - USE SPARINGLY, NEVER AT SENTENCE STARTS**
   - Filler words should appear naturally MID-sentence, NOT at the beginning
   - Most sentences should start clean and direct
   
   ✅ GOOD:
   - "The video makes a strong case for AI infrastructure."
   - "Ackman's argument here is, like, really compelling when you think about it."
   - "They pivot to technology and, you know, it totally changes the conversation."
   
   ❌ BAD:
   - "So, um, the video talks about..." (filler at start)
   - "And, uh, Ackman says that..." (filler at start)
   - "Well, you know, they mention..." (multiple fillers at start)
   
   **FILLER PLACEMENT RULES:**
   - Never start with: "Um,", "Uh,", "So,", "Well,", "Like,"
   - Use mid-sentence: "It's, um, really interesting" or "The point is, you know, crucial"
   - Maximum 1 filler word per sentence
   - Most sentences should have NO fillers

6. **SOUND LIKE YOU WATCHED IT, NOT READ A TRANSCRIPT**
   - Discuss the flow of arguments
   - Mention when speakers agree/disagree
   - Note energy shifts or surprising moments
   - Connect ideas across the video
   
   ✅ GOOD: "What's interesting is how they connect these dots—New York politics affecting global finance, which then ties into why tech infrastructure matters so much. It's not three separate topics, it's one big argument."
   
   ❌ BAD: "First topic is New York. Second topic is AI. Third topic is..."

**SUMMARY STRUCTURE FOR YOUTUBE VIDEOS:**

Opening: "So this video dives into [main theme]. Here's what stood out..."

Body: Group by themes/arguments, use vague time references:
- "Early on, they tackle [theme 1]..."
- "The conversation then shifts to [theme 2]..."
- "Towards the end, they debate [theme 3]..."
- Optional quote: 1-2 powerful statements (no timestamp)

Closing: "The big takeaway here is [main message]. For you, this matters because [relevance]."

**CRITICAL REMINDERS:**
- NO specific timestamps (no "1:37", "3:12", "7-minute mark")
- YES to vague references ("early on", "later", "towards the end")
- NO line-by-line narration
- NO filler words at sentence starts
- YES to thematic summaries
- YES to natural flow
- YES to explaining WHY it matters

IMPORTANT INSTRUCTIONS:
1. Automatically detect when the topic shifts (every 3-5 exchanges)
2. Create descriptive section names based on the content being discussed
3. For each dialogue line, include:
   - speaker: Host name
   - line: The dialogue text (CAN include names naturally in conversation)
   - timestamp: ACCURATE seconds from start (estimate speaking time realistically)
   - keywords: Array of 0-2 MOST important terms ONLY (NO NAMES in keywords!)
   - section: Section name ONLY when topic changes (e.g., "Introduction", "Main Discussion", "Expert Analysis", "Conclusion")

TIMESTAMP RULES:
- Estimate realistic speaking time for each line
- Short lines (1 sentence): ~3-4 seconds
- Medium lines (2 sentences): ~5-7 seconds
- Long lines (3+ sentences): ~8-12 seconds
- First line always: "timestamp": 0
- Second line example: "timestamp": 4 (if first line was short)
- Third line example: "timestamp": 9 (cumulative)
- Account for natural pauses between speakers (~1 second)

KEYWORD RULES - ULTRA SELECTIVE:
- Average 0-2 keywords per line (NOT 2-3)
- Many lines will have NO keywords - and that's GOOD
- Only highlight information that listeners would want to quickly scan back to find

✅ DO include as keywords (0-2 per line maximum):
- Specific numbers with units: "52°F", "2.3%", "$485.67", "9pm", "7-9 hours"
- Company/brand names: "Apple", "Meta", "Amazon", "Tesla", "OpenAI"
- Technical terms: "IPO", "AI", "machine learning", "investment"
- Important concepts: "inflation", "recession", "election", "climate"

❌ DO NOT include as keywords:
- Host names: "Emma", "James", "Jackie", "Kenny" (NEVER as keywords)
- User's name: "${userName || 'listener name'}" (NEVER as keywords)
- Greetings: "hey", "hello", "welcome"
- Common words: "today", "tonight", "video", "watch"

SECTION NAMING RULES:
- ALWAYS include a "section" field when the topic shifts
- First line MUST have: "section": "Introduction"
- Last few lines MUST have: "section": "Wrap Up"

ENGAGEMENT LEVEL - NATURAL CONVERSATION:
- Exclamation marks should be RARE (use in only 10-15% of lines)
- Most lines should end with periods (.) for natural tone
- Show energy through content, not excessive punctuation

CRITICAL JSON RULES:
- Return ONLY a valid JSON array, nothing else
- NO trailing commas before closing brackets
- Escape quotes inside dialogue with backslash
- Keep it for ${includeGuest ? '2-3 minute podcast (20-25 exchanges total)' : '1-2 minute podcast (15-20 exchanges total)'}
- ⚠️ CRITICAL: COMPLETE all JSON properly - ALWAYS close the array with ]
- ⚠️ If you run out of space, STOP at a complete entry and close the array properly

Format EXACTLY like this:
[
  {
    "speaker": "${validSpeakers[0]}",
    "line": "Hey ${userName || 'there'}, welcome to the InnerLoop!",
    "timestamp": 0,
    "keywords": [],
    "section": "Introduction"
  },
  {
    "speaker": "${validSpeakers[1]}",
    "line": "That's right! Let's dive into today's topic.",
    "timestamp": 4,
    "keywords": []
  }
]

✅ DO include as keywords (0-2 per line maximum):
- Specific numbers with units: "52°F", "2.3%", "$485.67", "9pm", "7-9 hours"
- Company/brand names: "Apple", "Meta", "Amazon", "Tesla", "NVDA"
- Technical terms: "melatonin", "REM sleep", "quantum computing", "AI"
- Important medical/health terms: "caffeine", "exercise", "symptoms"
- Critical locations: "New York", "California" (only if central to discussion)
- Key concepts: "inflation", "recession", "election", "climate"

❌ DO NOT include as keywords (even though they CAN be said in dialogue):
- Host names: "Emma", "James", "Jackie", "Kenny" (NEVER as keywords)
- User's name: "${userName || 'listener name'}" (NEVER as keywords)
- Greetings: "hey", "hello", "welcome", "good morning/afternoon/evening"
- Time words: "today", "tonight", "yesterday", "tomorrow"
- Common words: "afternoon", "morning", "walk", "park", "street", "hat", "jacket"
- Generic locations: "park", "street", "downtown", "area"
- Filler words: "right", "now", "here", "there", "thing", "way"
- Action verbs: "heading", "going", "grab", "take", "get"

NOTE: Hosts CAN and SHOULD use names naturally in conversation (e.g., "Hey Franklyn" or "Emma, what do you think?"), but names should NOT be in the keywords array.

SECTION NAMING RULES:
- ALWAYS include a "section" field when the topic shifts
- First line MUST have: "section": "Introduction"
- When discussing weather: "section": "Weather Forecast"
- When discussing news: "section": "Top Story: [brief topic]"
- When discussing stocks: "section": "Market Update"
- When discussing schedule: "section": "Today's Schedule"
- When discussing health/sleep: "section": "Health Tips"
- Last few lines MUST have: "section": "Wrap Up"
- For general topics: Use descriptive names based on what's being discussed

ENGAGEMENT LEVEL - MAKE IT LIVELY BUT NATURAL:
- Show energy through word choice and pacing, NOT excessive punctuation
- Use vocal expressions sparingly: "Oh", "Wow", "Wait really", "That's incredible", "Hold on"
- Exclamation marks should be RARE (use in only 10-15% of lines, for truly exciting moments)
- Most lines should end with periods (.) for natural, conversational tone
- Question marks (?) are fine for genuine questions
- Show genuine curiosity: "How does that work?", "Tell me more", "That's fascinating"
- React to surprising information: "I had no idea", "That changes everything", "Seriously?"
- Build momentum through content, not punctuation
- Hosts should "pass the baton" smoothly: "What do you think?" or "Jump in here" or "You know about this right?"
${includeGuest ? `- When ${guestSpeaker} enters, there should be a noticeable energy boost through their words and timing
- The two starting hosts should WELCOME the energy: "Perfect timing" or "We needed you" or "Tell us everything"` : ''}
- Vary the pacing: some quick back-and-forth exchanges, some longer explanations
- Brief overlaps or finishing each other's thoughts are natural
- Sound like real people having a conversation they're genuinely interested in, NOT reading a script
- Address the USER directly: "Here's what you need to know", "This affects you because", "You'll want to..."
- NEVER address a general audience: No "everyone", "listeners", "folks", "people out there"
- Hosts deliver information TO the user, not chat about random topics with each other
- Stay on topic - if discussing traffic, give traffic info and move on
- If discussing news, explain what it means for THEM specifically

PUNCTUATION GUIDELINES:
- Period (.) - Use 80% of the time for statements
- Question mark (?) - Use for genuine questions
- Exclamation mark (!) - Use SPARINGLY, only for:
  * Genuine surprise: "No way!"
  * Strong reactions: "That's huge!"
  * Greetings: "Hey!" (but not always)
  * Maximum 2-3 exclamation marks per 10 lines
- Avoid multiple punctuation (!!!, !??, etc.) - never use these

CRITICAL JSON RULES:
- Return ONLY a valid JSON array, nothing else
- NO trailing commas before closing brackets
- Escape quotes inside dialogue with backslash
- Host 1 name is Emma, Host 2 name is James, Host 3 name is Jackie, Host 4 name is Kenny
- Keep it for ${includeGuest ? '2-3 minute podcast (20-25 exchanges total)' : '1-2 minute podcast (15-20 exchanges total)'}
- COMPLETE all JSON properly - no truncated responses

Format EXACTLY like this:
[
  {
    "speaker": "${validSpeakers[0]}",
    "line": "Hey ${userName || 'there'}, welcome to the InnerLoop! Today it's 54°F outside.",
    "timestamp": 0,
    "keywords": ["54°F"],
    "section": "Introduction"
  },
  {
    "speaker": "${validSpeakers[1]}",
    "line": "That's right! And Emma, I think we should talk about Apple's stock jumping 2.3%.",
    "timestamp": 5,
    "keywords": ["Apple", "2.3%"]
  }
]

CRITICAL: Ensure the JSON array is COMPLETE and VALID. Close all brackets properly!

BACKGROUND REACTIONS:
- Add "background": true for short reactions that happen WHILE another speaker continues
- Background reactions should be brief (6-8 words max): "mhm", "yeah", "hmm", "right", "oh wow", "for sure", "absolutely"
- Use background reactions 8-10 times throughout the podcast for natural flow
- These happen DURING another speaker's turn, not as separate exchanges

CRITICAL RULES FOR NATURAL CONVERSATION:
- Hosts CAN and SHOULD say names naturally: "Hey ${userName || 'there'}", "Emma, what do you think?", "James mentioned..."
- Hosts are professionals - NO personal anecdotes like "today I did X" or "this morning I went Y"
- Focus on the content/news/topic itself, not what hosts personally experienced
- Natural greetings: "Hey" or "Hey ${userName || 'there'}" NOT "Hey ${userName || 'there'}!" (no exclamation)
- Keep tone conversational but professional

⚠️⚠️⚠️ CRITICAL REMINDER ⚠️⚠️⚠️
YOU MUST CLOSE THE JSON ARRAY WITH ] 
IF YOU ARE RUNNING OUT OF TOKENS:
1. Finish the current dialogue entry completely
2. Add a proper closing brace }
3. IMMEDIATELY add the closing bracket ]
4. DO NOT start a new entry if you can't complete it

SPEAKER LABELS - MUST USE EXACTLY:
${validSpeakers.map(speaker => `- Use "${speaker}" (NOT "Host X" or numbers)`).join('\n')}

DO NOT USE: "Host 1", "Host 2", "Host 3", "Host 4", "Guest", or any other labels. ONLY use: ${validSpeakers.join(', ')}

CRITICAL BRANDING:
- This is "the InnerLoop" NOT "the show" or "the InnerLoop show"
- Say "Welcome to the InnerLoop" or "Welcome back to the InnerLoop"
- Never say "Welcome to the show" or "the InnerLoop show"

Tone: ${includeGuest 
  ? `THREE SPEAKERS (${validSpeakers[0]} and ${validSpeakers[1]} start, then ${guestSpeaker} joins naturally around 30-50 seconds in) having a LIVELY, DYNAMIC conversation. Think of it like friends talking at a coffee shop - interruptions, excitement, building off each other's ideas. The guest should bring ENERGY when they enter - like a friend arriving with breaking news or exciting insights!` 
  : `TWO SPEAKERS (${validSpeakers[0]}, ${validSpeakers[1]}) having a REAL, ENGAGING conversation with natural rhythm. Show genuine curiosity and excitement about the topic!`
}`
              },
              {
                role: 'user',
                content: `Create podcast dialogue about: ${topic}
                ⚠️ REMINDER: You MUST return a COMPLETE JSON array that ends with ]. Do not let the response get cut off. If approaching token limit, finish the current entry and close the array immediately.`
         }
      ], {
        temperature: 0.7,
        max_tokens: includeGuest ? 3500 : 2800
      });
      

// ✅ ADD: Better validation
if (!data || !data.choices || !data.choices[0] || !data.choices[0].message || !data.choices[0].message.content) {
  console.error('❌ Invalid API response structure:', data);
  throw new Error('Invalid API response - missing content');
}

const content = data.choices[0].message.content;
console.log('📝 Parsing dialogue...');

const dialogue = parseDialogueJSON(content);

// ✅ Validate the parsed dialogue
try {
  validateDialogueStructure(dialogue);
  console.log(`✅ Dialogue structure validated: ${dialogue.length} exchanges`);
} catch (validationError) {
  console.error('❌ Dialogue validation failed:', validationError.message);
  throw new Error(`Invalid dialogue structure: ${validationError.message}`);
}
      // ✅ Validation + Fallback Logic
      console.log('🔍 Validating dialogue structure...');

      dialogue.forEach((line, idx) => {
        if (!line.keywords || !Array.isArray(line.keywords) || line.keywords.length === 0) {
          console.warn(`⚠️ Line ${idx} missing keywords, auto-generating...`);
          
          const words = line.line.split(/\s+/);
          const autoKeywords = [];
          
          words.forEach(word => {
            if (/\d/.test(word) && word.length < 10) {
              autoKeywords.push(word);
            }
          });
          
          words.forEach(word => {
            if (/^[A-Z][a-z]+/.test(word) && word.length > 3 && autoKeywords.length < 5) {
              autoKeywords.push(word);
            }
          });
          
          const importantTerms = ['weather', 'temperature', 'market', 'stock', 'news', 'today', 'schedule'];
          words.forEach(word => {
            const lower = word.toLowerCase();
            if (importantTerms.includes(lower) && autoKeywords.length < 5) {
              autoKeywords.push(word);
            }
          });
          
          line.keywords = autoKeywords.slice(0, 5);
        }
        
        console.log(`  Line ${idx}: ${line.keywords.length} keywords`, line.keywords);
      });

      // Ensure sections are present
      let hasIntroduction = dialogue.some(line => line.section === 'Introduction');
      let hasWrapUp = dialogue.some(line => line.section === 'Wrap Up');

      if (!hasIntroduction) {
        console.warn('⚠️ Missing Introduction section, adding to first line');
        dialogue[0].section = 'Introduction';
      }

      if (!hasWrapUp) {
        console.warn('⚠️ Missing Wrap Up section, adding to last line');
        dialogue[dialogue.length - 1].section = 'Wrap Up';
      }

      const sectionsCount = dialogue.filter(line => line.section).length;
      if (sectionsCount < 3) {
        console.warn('⚠️ Not enough sections, auto-generating...');
        const middleIndex = Math.floor(dialogue.length / 2);
        if (!dialogue[middleIndex].section) {
          dialogue[middleIndex].section = 'Main Discussion';
        }
      }

      console.log(`✅ Validation complete: ${dialogue.length} lines, ${dialogue.filter(l => l.section).length} sections`);

      // Validate background reactions
      for (const item of dialogue) {
        if (item.background === true) {
          const wordCount = item.line.trim().split(/\s+/).length;
          if (wordCount > 3) {
            console.warn(`⚠️ Background reaction too long: "${item.line}"`);
            delete item.background;
          }
        }
      }
      
      // Count background reactions
      const backgroundCount = dialogue.filter(d => d.background === true).length;
      if (backgroundCount > 0) {
        console.log(`🎭 Background reactions: ${backgroundCount}`);
      }
      
      if (!Array.isArray(dialogue) || dialogue.length === 0) {
        throw new Error('Invalid dialogue format');
      }
      
      // Validate dialogue structure and speakers
      for (const item of dialogue) {
        if (!item.speaker || !item.line || typeof item.line !== 'string') {
          throw new Error('Invalid dialogue item structure');
        }
        if (!validSpeakers.includes(item.speaker)) {
          console.warn(`⚠️ Unexpected speaker: ${item.speaker}, converting to ${validSpeakers[0]}`);
          item.speaker = validSpeakers[0];
        }
      }
      
      console.log(`✅ Generated ${dialogue.length} dialogue exchanges${includeGuest ? ` with guest speaker (${guestSpeaker})` : ''}`);
      
      if (includeGuest) {
        const guestFirstAppearance = dialogue.findIndex(d => d.speaker === guestSpeaker);
        if (guestFirstAppearance !== -1) {
          console.log(`\n🎤 GUEST INTRODUCTION:`);
          console.log(`   ${guestSpeaker} enters at exchange #${guestFirstAppearance + 1}`);
          console.log(`   Preview: "${dialogue[guestFirstAppearance].line.substring(0, 60)}..."`);
        }
        
        const speakerCounts = {};
        dialogue.forEach(d => {
          speakerCounts[d.speaker] = (speakerCounts[d.speaker] || 0) + 1;
        });
        
        console.log(`\n📊 SPEAKER PARTICIPATION:`);
        Object.entries(speakerCounts).forEach(([speaker, count]) => {
          const percentage = ((count / dialogue.length) * 100).toFixed(1);
          console.log(`   ${speaker}: ${count} exchanges (${percentage}%)`);
        });
      }
      
      return dialogue;
      
} catch (error) {
  lastError = error;
  console.warn(`⚠️ Attempt ${attempt + 1} failed:`, error.message || error);
  
  // ✅ ADD: Log the full error for debugging
  if (!error.message) {
    console.error('Full error object:', error);
  }
  
  // If it's a truncation error on first attempt, try with shorter content
  if (attempt === 0 && error.message && error.message.includes('truncated')) {
    console.log('🔄 Retrying with adjusted parameters...');
  }
  
  if (attempt < maxRetries - 1) {
    const waitTime = 1000 * (attempt + 1);
    console.log(`⏳ Waiting ${waitTime}ms before retry...`);
    await new Promise(resolve => setTimeout(resolve, waitTime));
  }
}

  }
  
  console.error('❌ All attempts failed');
  throw new Error(`Something went wrong. Please come back later!`);
};

const addBackgroundReactionsIfNeeded = (dialogue) => {
  const backgroundCount = dialogue.filter(d => d.background === true).length;
  
  if (backgroundCount >= 12) {
    console.log(`✅ Sufficient background reactions: ${backgroundCount}`);
    return dialogue;
  }
  
  console.log(`⚠️ Only ${backgroundCount} background reactions, adding more...`);
  
  const reactions = [
    "Mhm", "Yeah", "Right", "Oh wow", "That's actually Interesting", 
    "For sure", "Totally", "Hmm", "Oh really", "No way",
    "Exactly", "True", "Absolutely", "Oh", "Wow"
  ];
  
  const enhanced = [];
  let reactionIndex = 0;
  let addedCount = 0;
  const targetToAdd = Math.min(15 - backgroundCount, 10); // Add up to 10 more
  
  for (let i = 0; i < dialogue.length; i++) {
    enhanced.push(dialogue[i]);
    
    // Add reaction every 3-4 exchanges (but not after background reactions or at the very end)
    if (
      addedCount < targetToAdd &&
      i % 3 === 1 && 
      !dialogue[i].background && 
      i < dialogue.length - 2 &&
      dialogue[i].line.split(' ').length > 8 // Only after substantial lines
    ) {
      const currentSpeaker = dialogue[i].speaker;
      const nextSpeaker = dialogue[i + 1]?.speaker;
      
      // Pick someone OTHER than current speaker
      let reactionSpeaker;
      if (nextSpeaker && nextSpeaker !== currentSpeaker) {
        reactionSpeaker = nextSpeaker;
      } else {
        const allSpeakers = ["Host 1", "Host 2", "Host 3", "Host 4"];
        const otherSpeakers = allSpeakers.filter(s => s !== currentSpeaker);
        reactionSpeaker = otherSpeakers[Math.floor(Math.random() * otherSpeakers.length)];
      }
      
      enhanced.push({
        speaker: reactionSpeaker,
        line: reactions[reactionIndex % reactions.length],
        timestamp: dialogue[i].timestamp + Math.floor(dialogue[i].line.split(' ').length / 3),
        keywords: [],
        background: true
      });
      
      reactionIndex++;
      addedCount++;
    }
  }
  
  const newBackgroundCount = enhanced.filter(d => d.background).length;
  console.log(`✅ Background reactions after enhancement: ${newBackgroundCount}`);
  
  return enhanced;
};
const generateShortPodcastFallback = async (
  topic,
  speakers,
  userName,
  AZURE_OPENAI_ENDPOINT,
  AZURE_OPENAI_DEPLOYMENT,
  AZURE_OPENAI_API_KEY
) => {
  console.log('🆘 Using fallback short podcast generation');
  
  const response = await fetch(
    `${AZURE_OPENAI_ENDPOINT}/openai/deployments/${AZURE_OPENAI_DEPLOYMENT}/chat/completions?api-version=2024-02-15-preview`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'api-key': AZURE_OPENAI_API_KEY
      },
      body: JSON.stringify({
        messages: [
          {
            role: 'system',
            content: `Create a SHORT podcast dialogue (10-12 exchanges) between ${speakers.join(' and ')}.
            
Format as JSON array:
[
  {"speaker": "${speakers[0]}", "line": "dialogue text", "timestamp": 0, "keywords": [], "section": "Introduction"},
  {"speaker": "${speakers[1]}", "line": "dialogue text", "timestamp": 4, "keywords": []}
]

CRITICAL: 
- Keep it SHORT (10-12 exchanges only)
- ALWAYS close the array with ]
- Natural conversation tone
- End properly with "Wrap Up" section`
          },
          {
            role: 'user',
            content: `Topic: ${topic}\n\nCreate a brief 10-12 exchange podcast. MUST end with complete JSON array.`
          }
        ],
        temperature: 0.5,
        max_tokens: 2000
      })
    }
  );
  
  const data = await response.json();
  const dialogue = parseDialogueJSON(data.choices[0].message.content);
  validateDialogueStructure(dialogue);
  
  return dialogue;
};
const fetchEnhancedStockData = async () => {
  try {
    console.log('📊 Fetching enhanced stock data from backend...');
    
    const response = await fetch(
      'https://us-central1-fir-7c9c9.cloudfunctions.net/fetchStocks',
      {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      }
    );
    
    if (!response.ok) {
      console.error(`❌ Backend returned HTTP ${response.status}`);
      return null;
    }
    
    const data = await response.json();
    
    if (data.success && data.stocks) {
      console.log(`✅ Enhanced stock data loaded: ${data.stocks.length} stocks`);
      console.log(`📊 Market sentiment: ${data.marketSummary?.overview.marketSentiment}`);
      return data;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error fetching enhanced stock data:', error);
    return null;
  }
};

// 2. ADD THIS NEW FUNCTION - Web search for real-time questions
const performSmartWebSearch = async (query) => {
  try {
    console.log(`🔍 Smart web search: "${query}"`);
    
    const response = await fetch(
      `https://us-central1-fir-7c9c9.cloudfunctions.net/smartWebSearch?q=${encodeURIComponent(query)}`,
      {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      }
    );
    
    if (!response.ok) {
      console.error(`❌ Search failed: HTTP ${response.status}`);
      return null;
    }
    
    const data = await response.json();
    
    if (data.success) {
      console.log(`✅ Found answer (${data.confidence} confidence)`);
      return data;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Search error:', error);
    return null;
  }
};

/**
 * Build custom podcast content based on selected modules
 */
export const buildCustomPodcastContent = async (
  connectedModules,
  weatherData,
  newsData,
  scheduleData,
  stockData,
  userName,
  userLocation,
  trafficData = null,  // ✅ Add this parameter
    promptText = '' // ✅ ADD THIS PARAMETER
) => {
  console.log('🎙️ Building custom podcast content:', {
    modules: connectedModules,
    userName,
    location: userLocation
  });
  
  const sections = [];
  
  // ✅ Get current date info for natural language
  const dateInfo = getCurrentDateInfo();
  const now = new Date();
  const timeOfDay = getTimeOfDay();

// 1. WEATHER SECTION
  if (connectedModules.includes('weather') && weatherData) {
    console.log('🌤️ Processing weather data:', weatherData);
    
    const location = weatherData.location || {};
    const weather = weatherData.weather || {};
    const locationStr = location.state 
      ? `${location.city}, ${location.state}` 
      : location.city || userLocation;
    
    const condition = weather.condition || 'clear';
    const temp = weather.temperature || 70;
    const windSpeed = weather.windSpeed || 5;
    const humidity = weather.humidity || 50;
    const precipitation = weather.precipitation || 0;
    
    // ✅ Get coordinates from weatherData
    const lat = location.lat || weatherData.lat;
    const lon = location.lon || weatherData.lon;
    
    // ✅ Randomly decide if we mention nearby locations (60% chance)
    const mentionNearby = Math.random() > 0.4;
    
    // ✅ Fetch nearby cities automatically (no hardcoding!)
    let nearbyLocations = [];
    if (lat && lon && mentionNearby) {
      try {
        const nearbyCities = await findNearbyCities(
          lat, 
          lon, 
          process.env.REACT_APP_WEATHER_API_KEY || weatherData.apiKey
        );
        
        // Generate comparison text for each nearby city
        nearbyLocations = nearbyCities.map(city => 
          generateWeatherComparison(temp, condition, city)
        );
        
        console.log('✅ Nearby weather comparisons:', nearbyLocations);
      } catch (error) {
        console.warn('⚠️ Could not fetch nearby weather:', error);
      }
    } else if (!mentionNearby) {
      console.log('🎲 Skipping nearby locations for variety');
    }
    
    // ✅ Build weather content with time context
    let weatherContent = '';
    if (timeOfDay === 'morning') {
      weatherContent = `Good morning! For ${dateInfo.dayOfWeek}, ${dateInfo.month} ${dateInfo.day}, the weather in ${locationStr} is ${Math.round(temp)}°F and ${condition}.`;
    } else if (timeOfDay === 'afternoon') {
      weatherContent = `Right now on this ${dateInfo.dayOfWeek} afternoon, ${dateInfo.month} ${dateInfo.day}, it's ${Math.round(temp)}°F and ${condition} in ${locationStr}.`;
    } else if (timeOfDay === 'evening') {
      weatherContent = `This ${dateInfo.dayOfWeek} evening, ${dateInfo.month} ${dateInfo.day}, we're looking at ${Math.round(temp)}°F and ${condition} in ${locationStr}.`;
    } else {
      weatherContent = `Tonight, ${dateInfo.month} ${dateInfo.day}, it's ${Math.round(temp)}°F and ${condition} in ${locationStr}.`;
    }
    
    // ✅ Add nearby location comparisons SOMETIMES (not always!)
    if (nearbyLocations.length > 0) {
      const nearest = nearbyLocations[0];
      
      // Pick a random intro phrase for variety
      const intros = [
        `Meanwhile, ${nearest.name} is`,
        `Over in ${nearest.name}, it's`,
        `And ${nearest.name} is`,
        `Just checked ${nearest.name}, it's`,
        `Looking at ${nearest.name}, it's`
      ];
      const intro = intros[Math.floor(Math.random() * intros.length)];
      
      weatherContent += ` ${intro} ${nearest.comparison}.`;
      
      // Optionally mention second nearby city (30% chance, not always!)
      if (nearbyLocations.length > 1 && Math.random() > 0.7) {
        const second = nearbyLocations[1];
        weatherContent += ` And ${second.name} is ${second.comparison}.`;
      }
    }
    
    // ✅ Add weather advice (also randomize when this appears)
    const giveAdvice = Math.random() > 0.3; // 70% chance of advice
    
    if (giveAdvice) {
      if (precipitation > 0) {
        weatherContent += ` There's a ${Math.round(precipitation)}% chance of rain, so grab an umbrella.`;
      } else if (windSpeed > 15) {
        weatherContent += ` It's quite windy at ${Math.round(windSpeed)}mph, so grab a jacket.`;
      } else if (temp < 50) {
        weatherContent += ` Bundle up, it's chilly out there.`;
      } else if (temp > 85) {
        if (humidity > 70) {
          weatherContent += ` It's hot and humid at ${humidity}% humidity—stay hydrated.`;
        } else {
          weatherContent += ` Stay hydrated, it's going to be hot.`;
        }
      } else if (condition.toLowerCase().includes('rain')) {
        weatherContent += ' Don\'t forget your umbrella.';
      }
    }
    
    console.log('✅ Weather content generated:', weatherContent);
    
    sections.push({
      type: 'weather',
      content: weatherContent,
      nearbyLocations: nearbyLocations // Pass to dialogue generator
    });
  }
  
  // 2. NEWS SECTION
if (connectedModules.includes('news') && newsData) {
  // Check if this is local news (has mainCity property) or general news
  const isLocalNews = newsData.mainCity !== undefined;
  
  if (isLocalNews) {
    console.log('📍 Processing LOCAL news data');
    
    // ✅ ADD VALIDATION - Check if local news has articles
    if (!newsData.mainCity.articles || newsData.mainCity.articles.length === 0) {
      console.warn('⚠️ Local news has no articles, skipping news section');
      // Don't add news section if empty
    } else {
      const localNewsContent = formatLocalNewsForPodcast(newsData, userName);
      
      sections.push({
        type: 'news',
        content: localNewsContent,
        isLocal: true
      });
    }
  } else {
    console.log('🌐 Processing GENERAL news data');
    
    // Existing general news logic
    const topStories = [];
    
    Object.entries(newsData).forEach(([sourceId, sourceData]) => {
      if (sourceData.stories && sourceData.stories.length > 0) {
        const story = sourceData.stories[0];
        if (story && !topStories.some(s => s.title === story.title)) {
          topStories.push({
            title: story.title,
            source: sourceData.name || sourceId.toUpperCase(),
            time: story.time
          });
        }
      }
    });
    
    if (topStories.length > 0) {
      const newsIntro = `Here's what's making headlines on ${dateInfo.dayOfWeek}, ${dateInfo.month} ${dateInfo.day}:`;
      const storyList = topStories.slice(0, 5).map(s => {
        const timeContext = s.time ? ` from ${s.time}` : '';
        return `${s.title}${timeContext}`;
      }).join('. ');
      
      sections.push({
        type: 'news',
        content: `${newsIntro} ${storyList}.`,
        isLocal: false
      });
    }
  }
}
  
  // 3. STOCKS SECTION
if (connectedModules.includes('stocks') && stockData && stockData.length > 0) {
  console.log('📈 Building ENHANCED stock content with weekly analysis...');
  
  // Try to get enhanced data with weekly performance
  let enhancedData = null;
  let weeklyData = null;
  
  try {
    enhancedData = await fetchEnhancedStockData();
    weeklyData = await fetchWeeklyStockPerformance(); // New function
  } catch (e) {
    console.warn('⚠️ Could not fetch enhanced stock data, using basic data');
  }
  
  const tickerToName = {
    'TSLA': 'Tesla', 'AAPL': 'Apple', 'MSFT': 'Microsoft', 
    'GOOGL': 'Google', 'AMZN': 'Amazon', 'META': 'Meta',
    'NVDA': 'NVIDIA', 'SPX': 'the S&P 500', 'JPM': 'JPMorgan',
    'V': 'Visa', 'WMT': 'Walmart'
  };
  
  let stocksContent = '';
  
  // ✅ ENHANCED: Use market summary if available
  if (enhancedData?.marketSummary) {
    const summary = enhancedData.marketSummary;
    const sentiment = summary.overview.marketSentiment;
    const topGainer = summary.highlights.topGainer;
    const topLoser = summary.highlights.topLoser;
    
    // Natural, conversational opener based on market sentiment
    if (sentiment === 'bullish') {
      stocksContent = `Markets are looking pretty good today. `;
    } else if (sentiment === 'bearish') {
      stocksContent = `Markets are taking a bit of a hit today. `;
    } else {
      stocksContent = `Markets are all over the place today. `;
    }
    
    // Top gainer (use company name, explain simply)
    const gainerName = tickerToName[topGainer.ticker] || topGainer.ticker;
    stocksContent += `${gainerName} is up ${topGainer.percent}—that's the biggest winner. `;
    
    // ✅ NEW: Add weekly context if available
    if (weeklyData?.weekly?.[topGainer.ticker]) {
      const weeklyChange = weeklyData.weekly[topGainer.ticker].weekChange;
      if (Math.abs(parseFloat(weeklyChange)) > 5) {
        stocksContent += `For the week, it's ${weeklyChange > 0 ? 'up' : 'down'} ${Math.abs(weeklyChange).toFixed(1)}%—pretty significant move. `;
      }
    }
    
    // Add context if it's a significant move
    const gainerPercent = Math.abs(parseFloat(topGainer.percent));
    if (gainerPercent > 3) {
      stocksContent += `That's a pretty big jump. `;
    }
    
    // Top loser (conversational)
    const loserName = tickerToName[topLoser.ticker] || topLoser.ticker;
    stocksContent += `On the flip side, ${loserName} is down ${topLoser.percent}. `;
    
    // ✅ NEW: Weekly comparison for loser
    if (weeklyData?.weekly?.[topLoser.ticker]) {
      const weeklyChange = weeklyData.weekly[topLoser.ticker].weekChange;
      if (parseFloat(weeklyChange) < -5) {
        stocksContent += `It's been a rough week—down ${Math.abs(weeklyChange).toFixed(1)}% over the last five days. `;
      } else if (parseFloat(weeklyChange) > 0) {
        stocksContent += `But actually, it's still up for the week at ${weeklyChange}%. `;
      }
    }
    
    // ✅ ADD INSIGHTS - Only if interesting
    if (summary.highlights.unusualVolumeStocks > 0) {
      stocksContent += `Also seeing some unusual trading activity today—people are definitely paying attention. `;
    }
    
    if (summary.highlights.nearYearHighs > 0) {
      stocksContent += `A few stocks are trading near their highs for the year, which is worth noting. `;
    }
    
    // ✅ INDIVIDUAL STOCK INSIGHTS (pick 1-2 interesting ones)
    const stocks = enhancedData.stocks || stockData;
    const interestingStocks = stocks.filter(s => 
      s.insights?.significantMove || s.insights?.unusualVolume || s.insights?.nearYearHigh
    );
    
    if (interestingStocks.length > 0) {
      const stock = interestingStocks[0];
      const name = tickerToName[stock.ticker] || stock.ticker;
      
      if (stock.insights.nearYearHigh) {
        stocksContent += `${name} is hovering near its 52-week high right now—basically as high as it's been all year. `;
        
        // ✅ NEW: Add momentum context
        if (weeklyData?.weekly?.[stock.ticker]) {
          const weeklyChange = weeklyData.weekly[stock.ticker].weekChange;
          if (parseFloat(weeklyChange) > 3) {
            stocksContent += `And it's been climbing—up ${weeklyChange}% this week. `;
          }
        }
      } else if (stock.insights.unusualVolume) {
        stocksContent += `${name} is seeing way more trading than usual today—something's got investors interested. `;
      } else if (stock.insights.significantMove) {
        stocksContent += `${name} made a pretty big move today at ${stock.percent}. `;
      }
    }
    
    // ✅ NEW: Weekly market trend summary
    if (weeklyData?.marketTrend) {
      const trend = weeklyData.marketTrend;
      if (trend.weeklyDirection === 'strong_up') {
        stocksContent += `Looking at the bigger picture, markets have been climbing steadily all week. `;
      } else if (trend.weeklyDirection === 'strong_down') {
        stocksContent += `Zooming out, it's been a down week overall for most stocks. `;
      } else if (trend.weeklyDirection === 'volatile') {
        stocksContent += `It's been a choppy week—markets bouncing up and down. `;
      }
    }
    
  } else {
    // ✅ FALLBACK: Use basic stock data with natural language
    const topMovers = stockData
      .sort((a, b) => Math.abs(parseFloat(b.change)) - Math.abs(parseFloat(a.change)))
      .slice(0, 3);
    
    if (topMovers.length > 0) {
      stocksContent = `Looking at the markets, `;
      
      topMovers.forEach((stock, idx) => {
        const name = tickerToName[stock.ticker] || stock.ticker;
        const direction = stock.isUp ? 'up' : 'down';
        
        if (idx === 0) {
          stocksContent += `${name} is ${direction} ${stock.percent}`;
        } else if (idx === topMovers.length - 1) {
          stocksContent += `, and ${name} is ${direction} ${stock.percent}`;
        } else {
          stocksContent += `, ${name} is ${direction} ${stock.percent}`;
        }
      });
      
      stocksContent += `. `;
    }
  }
  
  // ✅ Add time context
  const dateInfo = getCurrentDateInfo();
  const timeOfDay = getTimeOfDay();
  
  if (timeOfDay === 'morning') {
    stocksContent = `Looking at pre-market this ${dateInfo.dayOfWeek} morning, ` + stocksContent;
  } else if (timeOfDay === 'afternoon') {
    stocksContent = `Here's where things stand this ${dateInfo.dayOfWeek} afternoon: ` + stocksContent;
  } else if (timeOfDay === 'evening') {
    stocksContent = `Markets closed today, ` + stocksContent;
  } else {
    stocksContent = `After hours, ` + stocksContent;
  }
  
  sections.push({
    type: 'stocks',
    content: stocksContent,
    enhancedData: enhancedData,
    weeklyData: weeklyData // ✅ Pass weekly data
  });
}

  // 4. TRAFFIC SECTION (Add this BEFORE schedule section)
if (connectedModules.includes('traffic') && trafficData) {
  console.log('🚗 Adding traffic data to podcast:', trafficData);
  
  const trafficContent = formatTrafficForPodcast(trafficData);
  
  sections.push({
    type: 'traffic',
    content: trafficContent
  });
}
  // 5. SCHEDULE SECTION
if (connectedModules.includes('schedule')) {
  console.log('📅 Schedule module selected');
  
  if (scheduleData && Object.keys(scheduleData).length > 0) {
    console.log('📅 Processing schedule data:', Object.keys(scheduleData).length, 'dates');
    
    // ✅ Import the formatter
    const { formatScheduleForPodcast } = await import('./schedulerHandlers');
    
    // ✅ Smart schedule detection based on promptText
    const scheduleQuery = promptText || 'today';
    const scheduleInfo = formatScheduleForPodcast(scheduleData, scheduleQuery);
    
    console.log('✅ Schedule formatted:', scheduleInfo);
    
    if (scheduleInfo.hasEvents) {
      sections.push({
        type: 'schedule',
        content: scheduleInfo.summary,
        eventCount: scheduleInfo.eventCount,
        dateLabel: scheduleInfo.dateLabel,
        events: scheduleInfo.events || scheduleInfo.allEvents
      });
    } else {
      sections.push({
        type: 'schedule',
        content: scheduleInfo.summary
      });
    }
  } else {
    console.log('⚠️ Schedule module selected but calendar is empty');
    sections.push({
      type: 'schedule',
      content: 'Your calendar is empty right now. Want me to help you add something?'
    });
  }
}
  
    if (promptText && shouldSearchWeb(promptText)) {
    console.log('⏰ Time-sensitive question detected, searching web...');
    
    const searchResult = await performSmartWebSearch(promptText);
    
    if (searchResult?.success) {
      sections.push({
        type: 'web_answer',
        content: searchResult.answer,
        source: searchResult.source,
        confidence: searchResult.confidence,
        query: promptText,
        talkingPoints: searchResult.talkingPoints
      });
    }
  } else if (promptText) {
    console.log('💭 General knowledge question - Azure AI will handle this naturally');
  }
  return sections;
};
/**
 * ✅ DECISION TREE: Determine if topic needs web search
 * Uses FREE Yahoo Search when needed
 */
const shouldUseWebSearchForTopic = async (topicText, AZURE_CONFIG) => {
  if (!topicText || topicText.trim().length === 0) {
    return { needsSearch: false, reasoning: 'No topic provided' };
  }
  
  console.log('📝 Analyzing topic for web search necessity...');
  console.log(`   Topic: "${topicText.substring(0, 80)}${topicText.length > 80 ? '...' : ''}"`);
  console.log(`   Date: ${new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })}`);
  
  try {
    // Use the existing AI-based detection
    const analysis = await analyzeQueryNeedsWebSearch(topicText, AZURE_CONFIG);
    
    console.log('\n📊 AI ANALYSIS:');
    console.log(`   ├─ Needs Search: ${analysis.needsSearch ? '✅ YES' : '❌ NO'}`);
    console.log(`   ├─ Confidence: ${analysis.confidence || 'medium'}`);
    console.log(`   ├─ Category: ${analysis.category || 'general'}`);
    console.log(`   └─ Reasoning: ${analysis.reasoning}`);
    
    if (analysis.needsSearch) {
      console.log(`\n🔍 Will use: Yahoo Search (Free)`);
      console.log(`   Query: "${analysis.searchQuery}"`);
    } else {
      console.log(`\n🧠 Will use: AI Training Data (Free)`);
    }
    
    return analysis;
    
  } catch (error) {
    console.error('❌ Decision tree error:', error);
    console.log('🔄 Using fallback detection...');
    return fallbackSearchDetection(topicText);
  }
};
/**
 * Generate custom Inner Loop podcast with NATURAL dialogue
 */
/**
 * Generate custom Inner Loop podcast with NATURAL dialogue
 * ✅ NOW USES SECUREAPI - NO API KEYS EXPOSED
 */
export const generateCustomInnerLoopPodcast = async (
  connectedModules,
  promptText,
  weatherData,
  newsData,
  scheduleData,
  selectedStocks,
  userName,
  userLocation,
  AZURE_CONFIG,  // ✅ Still accepted but ignored (backward compatibility)
  trafficData = null,
  fileContent = null,
  selectedHosts = null,
  talkingStyle = 'casual',
  includeGuest = null 
) => {
  console.log('🎙️ generateCustomInnerLoopPodcast called with:', {
    modules: connectedModules,
    hasPrompt: !!promptText && promptText.trim().length > 0,
    promptLength: promptText?.length || 0,
    hasFileContent: !!fileContent && fileContent.trim().length > 0,
    fileLength: fileContent?.length || 0,
    hasYouTubeContext: promptText?.includes('YouTube Video Context:') || false
  });

  const isYouTubePodcast = promptText?.includes('YouTube Video Context:') || false;
  
  if (isYouTubePodcast) {
    console.log('🎥 YouTube-based podcast detected!');
    console.log('   Context preview:', promptText.substring(0, 300) + '...');
  }

  const hasFileContent = fileContent && fileContent.trim().length > 0;
  const hasModules = connectedModules && connectedModules.length > 0;
  const hasPrompt = promptText && promptText.trim().length > 0;

  console.log('📋 Content assessment:', {
    hasFileContent,
    hasModules,
    hasPrompt,
    isYouTubePodcast,
    modules: connectedModules
  });

  // ============================================
  // ✅ SCENARIO 1: File-based podcast WITH MODULES
  // Fixed: Now properly combines file + modules
  // ============================================
  if (hasFileContent) {
    console.log('📄 FILE-BASED PODCAST - Generating dialogue about uploaded content');
    
    // ✅ STEP 1: Build module content if modules are selected
    let moduleSections = [];
    let moduleContent = '';
    
    if (hasModules && connectedModules.length > 0) {
      console.log(`🧩 Building content from ${connectedModules.length} modules...`);
      
      moduleSections = await buildCustomPodcastContent(
        connectedModules,
        weatherData,
        newsData,
        scheduleData,
        selectedStocks,
        userName,
        userLocation,
        trafficData,
        promptText
      );
      
      moduleContent = moduleSections.map(section => section.content).join('\n\n');
      
      console.log(`✅ Module content built: ${moduleSections.length} sections`);
      console.log(`   Total module content: ${moduleContent.length} characters`);
    }
    
    // ✅ STEP 2: Build comprehensive prompt
    const userInstruction = promptText?.trim() || '';
    
    let combinedPrompt = '';
    
    // ✅ If we have BOTH modules AND file
    if (moduleContent) {
      combinedPrompt = `⚠️⚠️⚠️ MULTI-SOURCE PODCAST ⚠️⚠️⚠️
You must create a podcast that covers ALL of the following content sources in this order:

📊 PART 1: CURRENT UPDATES (discuss these first):
${moduleContent}

📄 PART 2: DOCUMENT DISCUSSION (discuss this second):
---START OF DOCUMENT---
${fileContent}
---END OF DOCUMENT---

${userInstruction ? `📝 USER'S SPECIFIC REQUEST: "${userInstruction}"\n` : ''}

YOUR JOB:
1. START with current information (weather, news, stocks, traffic, schedule)
2. THEN transition naturally to the document content
3. Make connections between current events and document topics when relevant
4. Keep it conversational and engaging throughout
5. Address the user directly ("you", "your") not a general audience

STRUCTURE EXAMPLE:
Host 1: "Hey ${userName || 'there'}, let's kick things off with your updates for today..."
[Discuss weather/news/stocks/traffic/schedule]
Host 2: "Alright, now about that document you shared..."
[Transition to document discussion]
Host 1: "You know what's interesting? This actually connects to [something from updates]..."
[Make natural connections]

⚠️ CRITICAL: 
- Cover ALL modules (weather, news, etc.) if selected
- Cover ALL document content
- Don't skip either part
- Make smooth transitions between sections`;
    } else {
      // ✅ File only (no modules)
      combinedPrompt = `⚠️⚠️⚠️ DOCUMENT-FOCUSED PODCAST ⚠️⚠️⚠️
THE USER PROVIDED DOCUMENT CONTENT BELOW. CREATE A PODCAST DISCUSSING THIS SPECIFIC CONTENT.

📄 USER'S DOCUMENT CONTENT:
---START OF DOCUMENT---
${fileContent}
---END OF DOCUMENT---

${userInstruction ? `📝 USER'S SPECIFIC REQUEST: "${userInstruction}"\n` : ''}

YOUR JOB: Create a natural, engaging podcast dialogue where the hosts discuss the SPECIFIC CONTENT from this document. They should:
- Reference actual topics, concepts, and details from the document
- Ask questions about the content
- Debate or discuss different aspects
- Highlight key points the user mentioned
- Make it conversational and educational
- Sound like they've read/analyzed this specific material

⚠️ CRITICAL AUDIENCE RULES:
1. The hosts are talking TO the user, not to a general audience
2. Say "you" not "everyone" or "listeners"
3. NEVER say: "for everyone listening", "if you're out there"
4. ALWAYS say: "for you", "you might want to", "this affects you because"`;
    }

    console.log('✅ Combined prompt constructed');
    console.log(`   Module content: ${moduleContent.length} chars`);
    console.log(`   Document content: ${fileContent.length} chars`);
    console.log(`   Total prompt: ${combinedPrompt.length} chars`);

    const startingHosts = selectedHosts && selectedHosts.length >= 2 
      ? [selectedHosts[0], selectedHosts[1]]
      : getRandomStartingHosts();

    console.log('🎤 Using starting hosts:', startingHosts);
    
    const shouldIncludeGuest = includeGuest === null ? Math.random() > 0.8 : includeGuest === true;

    // ✅ CRITICAL: This now uses SecureAPI internally (no AZURE_CONFIG needed)
    const dialogue = await generatePodcastDialogue(
      combinedPrompt,
      null,
      shouldIncludeGuest,
      startingHosts,
      userName,
      null,  // ✅ AZURE_OPENAI_ENDPOINT - not used (kept for compatibility)
      null,  // ✅ AZURE_OPENAI_DEPLOYMENT - not used (kept for compatibility)
      null,  // ✅ AZURE_OPENAI_API_KEY - not used (kept for compatibility)
      talkingStyle
    );

    return {
      dialogue: dialogue,
      metadata: {
        targetDuration: 3,
        estimatedDuration: dialogue.length * 0.3,
        wordCount: dialogue.reduce((sum, d) => sum + d.line.split(/\s+/).length, 0),
        sources: {
          modules: connectedModules,
          hasDocument: true,
          documentLength: fileContent.length,
          hasModules: hasModules
        }
      },
      scriptContent: combinedPrompt,
      sections: moduleSections,
      podcastType: hasModules ? 'file-with-modules' : 'file-only'
    };
  }

  // ============================================
  // ✅ SCENARIO 2: Topic-based podcast (prompt text OR YouTube)
  // ============================================
  if ((promptText && promptText.trim() && connectedModules.length === 0) || isYouTubePodcast) {
   console.log('📝 TOPIC-BASED PODCAST - Generating dialogue about topic');
  
  let webSearchData = null;
  let searchMetadata = null;
  let searchFailed = false;
  let useAIKnowledge = false;
  
  if (!isYouTubePodcast) {
    console.log('\n╔════════════════════════════════════════════════════╗');
    console.log('║ 🌳 SMART DECISION TREE: Analyzing Topic          ║');
    console.log('╚════════════════════════════════════════════════════╝');
    
    try {
      const searchDecision = await analyzeQueryNeedsWebSearch(promptText);
      
      if (searchDecision.needsSearch) {
        console.log('🔍 DECISION: Topic requires current information');
        console.log(`   Original Query: "${searchDecision.originalQuery}"`);
        console.log(`   Smart Query: "${searchDecision.searchQuery}"`);
        console.log(`   Event Status: ${searchDecision.eventVerification}`);
        
        try {
          const searchResults = await performUniversalWebSearch(searchDecision);
          
          // ✅ SMART HANDLING: No results OR search failed
          if (!searchResults || searchResults.noResults) {
            console.log('⚠️ NO WEB RESULTS FOUND');
            console.log('   This could mean:');
            console.log('   - Event is not currently happening');
            console.log('   - Event doesn\'t exist');
            console.log('   - Search service issue');
            console.log('');
            console.log('✅ FALLBACK: Will use AI training knowledge');
            
            useAIKnowledge = true;
            searchMetadata = {
              searchAttempted: true,
              searchQuery: searchDecision.searchQuery,
              noResults: true,
              fallbackToAI: true,
              reasoning: 'No current web results found - using AI knowledge base'
            };
            
          } else if (searchResults.success && searchResults.results && searchResults.results.length > 0) {
            // ✅ SUCCESS: We have web results
            webSearchData = formatSearchResultsForPodcast(searchResults, userName);
            
            searchMetadata = {
              searchEngine: 'Yahoo Search',
              query: searchDecision.searchQuery,
              resultsCount: searchResults.results.length,
              topSource: searchResults.results[0].source,
              topTitle: searchResults.results[0].title,
              searchDate: searchResults.searchedAt || new Date().toISOString(),
              category: searchDecision.category,
              rawResults: searchResults.results,
              searchAttempted: true,
              noResults: false
            };
            
            console.log('✅ WEB SEARCH SUCCESSFUL');
            console.log(`   Found: ${searchResults.results.length} results`);
            console.log(`   Top: "${searchResults.results[0].title.substring(0, 50)}..."`);
            
          } else {
            // ✅ FALLBACK: Something went wrong
            console.error('❌ Search returned unexpected format');
            useAIKnowledge = true;
            searchMetadata = {
              searchAttempted: true,
              searchFailed: true,
              fallbackToAI: true
            };
          }
        } catch (searchError) {
          console.error('❌ Web search failed:', searchError.message);
          useAIKnowledge = true;
          searchMetadata = {
            searchAttempted: true,
            searchFailed: true,
            fallbackToAI: true,
            error: searchError.message
          };
        }
        
      } else {
        console.log('✅ DECISION: AI can answer from training data');
        console.log(`   Reasoning: ${searchDecision.reasoning}`);
        useAIKnowledge = true;
      }
      
    } catch (decisionError) {
      console.error('❌ Decision tree error:', decisionError);
      console.log('🔄 Falling back to AI knowledge');
      useAIKnowledge = true;
    }
    
    console.log('════════════════════════════════════════════════════\n');
  }
  
  const durationMinutes = parseDuration(promptText) || 3;
  
  let cleanTopic = promptText
    .replace(/create\s+(a\s+)?podcast\s+(on|about|for)/gi, '')
    .replace(/make\s+(a\s+)?podcast\s+(on|about|for)/gi, '')
    .replace(/generate\s+(a\s+)?podcast\s+(on|about|for)/gi, '')
    .trim();
  
  if (!cleanTopic) cleanTopic = promptText;
  
  // ✅ Add web search facts OR add instruction to use AI knowledge
  if (searchMetadata && searchMetadata.rawResults && !useAIKnowledge) {
    console.log('📝 Adding web search facts to topic...');
    
    const facts = searchMetadata.rawResults.slice(0, 5);
    let factsText = '\n\nCurrent Information from Web:\n';
    
    facts.forEach((fact, idx) => {
      factsText += `\n${idx + 1}. ${fact.title}`;
      if (fact.snippet) {
        factsText += `\n   ${fact.snippet}`;
      }
    });
    
    cleanTopic = cleanTopic + factsText + '\n\nDiscuss this current information naturally.';
    
  } else if (useAIKnowledge) {
    console.log('🧠 Using AI knowledge - adding context instruction...');
    
    cleanTopic = cleanTopic + `\n\nIMPORTANT: Web search found no current results for this query. This could mean:
- The event is not currently happening
- The information is historical/general knowledge
- You should answer from your training data if you have reliable information

Answer naturally based on what you know. If this is about a current event you don't have information about, acknowledge that honestly.`;
  }
  
  const startingHosts = selectedHosts && selectedHosts.length >= 2 
    ? [selectedHosts[0], selectedHosts[1]]
    : getRandomStartingHosts();

  const shouldIncludeGuest = includeGuest === null 
    ? Math.random() > 0.8
    : includeGuest === true;

  const dialogue = await generatePodcastDialogue(
    cleanTopic,
    null,
    shouldIncludeGuest,
    startingHosts,
    userName,
    null,
    null,
    null,
    talkingStyle
  );

  return {
    dialogue: dialogue,
    metadata: {
      targetDuration: durationMinutes,
      estimatedDuration: dialogue.length * 0.3,
      wordCount: dialogue.reduce((sum, d) => sum + d.line.split(/\s+/).length, 0),
      webSearchUsed: !!webSearchData && !useAIKnowledge,
      searchMetadata: searchMetadata,
      usedAIKnowledge: useAIKnowledge
    },
    scriptContent: cleanTopic,
    sections: [],
    podcastType: isYouTubePodcast ? 'youtube-video' : 'topic',
    searchInfo: searchMetadata ? {
      used: !useAIKnowledge,
      engine: useAIKnowledge ? 'AI Training Data' : 'Yahoo Search',
      query: searchMetadata.searchQuery || searchMetadata.query,
      resultsCount: searchMetadata.resultsCount || 0,
      fallbackToAI: useAIKnowledge,
      reasoning: useAIKnowledge 
        ? 'No web results found - used AI knowledge instead'
        : `Found ${searchMetadata.resultsCount} current results`
    } : {
      used: false,
      reason: 'Topic does not require current information'
    }
  };
};
  // ============================================
  // ✅ SCENARIO 3: Module-based custom podcast
  // ============================================
  if (hasModules) {
    console.log('🧩 MODULE-BASED PODCAST - Generating dialogue from selected modules');
    
    let sections = await buildCustomPodcastContent(
      connectedModules,
      weatherData,
      newsData,
      scheduleData,
      selectedStocks,
      userName,
      userLocation,
      trafficData,
      promptText
    );

    console.log(`📦 Module content built: ${sections.length} sections`);

    let scriptContent = sections.map(section => section.content).join(' ');
    
    if (promptText && promptText.trim() && !promptText.includes('YouTube Video Context:')) {
      console.log('🔍 Enriching with web search...');
      // ✅ This now uses SecureAPI internally
      sections = await enrichContentWithWebSearch(
        promptText, 
        sections, 
        userName
      );
      scriptContent = sections.map(section => section.content).join(' ');
    }

    if (!scriptContent.trim()) {
      throw new Error('No content generated from selected modules. Please try again.');
    }

    console.log(`✅ Script content generated: ${scriptContent.length} characters`);

    const durationMinutes = parseDuration(promptText) || 3;

    const startingHosts = selectedHosts && selectedHosts.length >= 2 
      ? [selectedHosts[0], selectedHosts[1]]
      : getRandomStartingHosts();

    console.log('🎤 Using starting hosts:', startingHosts);

    const shouldIncludeGuest = includeGuest === null ? Math.random() > 0.8 : includeGuest === true;

    // ✅ This now uses SecureAPI internally
    const dialogue = await generatePodcastDialogue(
      scriptContent,
      null,
      shouldIncludeGuest,
      startingHosts,
      userName,
      null,  // ✅ Not used anymore
      null,  // ✅ Not used anymore
      null,  // ✅ Not used anymore
      talkingStyle
    );

    return {
      dialogue: dialogue,
      metadata: {
        targetDuration: durationMinutes,
        estimatedDuration: dialogue.length * 0.3,
        wordCount: dialogue.reduce((sum, d) => sum + d.line.split(/\s+/).length, 0)
      },
      scriptContent,
      sections,
      podcastType: 'custom'
    };
  }

  // ============================================
  // ✅ SCENARIO 4: No content - ERROR
  // ============================================
  console.error('❌ NO CONTENT SOURCES FOUND');
  console.error('   Modules:', connectedModules);
  console.error('   Has prompt:', hasPrompt);
  console.error('   Has file:', hasFileContent);
  console.error('   Is YouTube:', isYouTubePodcast);
  
  throw new Error('Please select at least one module, enter a topic, upload a file, or provide YouTube content for your podcast.');
};


/**
 * Helper functions
 */
const getTodayEvents = (scheduleData) => {
  const today = new Date();
  const todayKey = today.getDate();
  return scheduleData[todayKey] || [];
};

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  try {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  } catch (e) {
    return timeStr;
  }
};

export default {
  generateCustomInnerLoopPodcast,
  buildCustomPodcastContent,
  generatePodcastDialogue,
   NATURAL_SPEECH_GUIDELINES,
  addBackgroundReactionsIfNeeded,
   fetchEnhancedStockData,
  performSmartWebSearch,
  detectTimeQuestion,
  STOCK_DISCUSSION_GUIDELINES,
  WEB_SEARCH_INTEGRATION,
    analyzeQueryNeedsWebSearch,
  performUniversalWebSearch,
  formatSearchResultsForPodcast,
  enrichContentWithWebSearch
};